import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"
import { computeTrackBoundaries } from "../lib/track_boundary"
import { rdpSimplify, catmullRomToBezier, traceSplinePath } from "../lib/spline"

// F1 24/25 team ids are 0–9; F1 26 reissued the grid as 476–486 (per the
// official UDP spec appendix) so both ranges coexist here.
const F1_TEAM_COLORS = {
  // F1 24 / F1 25
  0: "#27F4D2", // Mercedes
  1: "#E8002D", // Ferrari
  2: "#3671C6", // Red Bull
  3: "#64C4FF", // Williams
  4: "#229971", // Aston Martin
  5: "#FF87BC", // Alpine
  6: "#6692FF", // RB
  7: "#B6BABD", // Haas
  8: "#FF8000", // McLaren
  9: "#52E252", // Sauber
  // F1 26 (2026 Season Pack) — same livery palette for the 9 returning teams,
  // approximations for Audi (racing green over black) and Cadillac (silver / black).
  476: "#27F4D2", // Mercedes '26
  477: "#E8002D", // Ferrari '26
  478: "#3671C6", // Red Bull '26
  479: "#64C4FF", // Williams '26
  480: "#229971", // Aston Martin '26
  481: "#FF87BC", // Alpine '26
  482: "#6692FF", // RB '26
  483: "#B6BABD", // Haas '26
  484: "#FF8000", // McLaren '26
  485: "#00583E", // Audi '26
  486: "#A8A8A8", // Cadillac '26
}
const FALLBACK_COLOR = "#6A00FF"

let _measureCtx = null
function getTextWidth(text, fontSize = 10, fontWeight = "normal") {
  if (!_measureCtx) {
    const canvas = document.createElement("canvas")
    _measureCtx = canvas.getContext("2d")
  }
  _measureCtx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  return _measureCtx.measureText(text).width
}

// Toggle spline rendering: set to false to use old stride-based polylines for comparison
const USE_SPLINE = true

const OVERLAY_WIDTH = 12

const EVENT_CONFIG = {
  yellow_flag: {
    color: "#eab308",
    bgColor: "rgba(234,179,8,0.4)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />',
  },
  red_flag: {
    color: "#b91c1c",
    bgColor: "rgba(185,28,28,0.4)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />',
  },
  blue_flag: {
    color: "#3b82f6",
    bgColor: "rgba(59,130,246,0.3)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />',
  },
  green_flag: {
    color: "#22c55e",
    bgColor: "rgba(34,197,94,0.3)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />',
  },
  crash: {
    color: "#ef4444",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.049.58.025 1.193-.14 1.743" />',
  },
  pitstop: {
    color: "#3b82f6",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.049.58.025 1.193-.14 1.743" />',
  },
  penalty: {
    color: "#f97316",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />',
  },
  warning: {
    color: "#eab308",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />',
  },
  position_gained: {
    color: "#22c55e",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />',
  },
  position_lost: {
    color: "#ef4444",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />',
  },
  safety_car: {
    color: "#f59e0b",
    bgColor: "rgba(245,158,11,0.4)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.25-8.25-3.286ZM12 15.75h.007v.008H12v-.008Z" />',
  },
  collision: {
    color: "#dc2626",
    bgColor: "rgba(220,38,38,0.4)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />',
  },
  overtake: {
    color: "#10b981",
    bgColor: "rgba(16,185,129,0.3)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />',
  },
  fastest_lap: {
    color: "#8b5cf6",
    bgColor: "rgba(139,92,246,0.3)",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />',
  },
}

const FLAG_VALUE_MAP = {
  1: "green_flag",
  2: "blue_flag",
  3: "yellow_flag",
  4: "red_flag",
}

const DAMAGE_CHANNELS = [
  "front_left_wing_damage",
  "front_right_wing_damage",
  "rear_wing_damage",
  "floor_damage",
  "diffuser_damage",
  "sidepod_damage",
  "engine_damage",
  "gearbox_damage",
]

const DAMAGE_LABELS = {
  front_left_wing_damage: "Front Left Wing",
  front_right_wing_damage: "Front Right Wing",
  rear_wing_damage: "Rear Wing",
  floor_damage: "Floor",
  diffuser_damage: "Diffuser",
  sidepod_damage: "Sidepod",
  engine_damage: "Engine",
  gearbox_damage: "Gearbox",
}

const LAYERS_STORAGE_KEY = "track-map-layers"
const MINIMAP_STORAGE_KEY = "track-map-minimap-hidden"
const ZOOM_STORAGE_KEY = "track-map-zoom"
const VIEW_MODE_STORAGE_KEY = "apexracing.trackMap.viewMode"
const DEFAULT_LAYERS = {
  speed: false,
  sectors: true,
  events: false,
  braking: true,
  throttle: true,
}
const VIEW_MODE_2D = "2d"
const VIEW_MODE_25D = "2.5d"
const TILT_K = 0.7 // dimetric tilt factor — z-axis slope multiplier in 2.5D
const LIFT_PER_METER = 0.5 // screen pixels per meter of altitude (absolute scaling — Spa lifts more than COTA)
const MAX_LIFT_FRACTION = 0.3 // cap total altitude lift at this fraction of usable canvas height
const FLAT_TRACK_THRESHOLD_M = 2.0 // tracks with <2m of elevation render as 2D-flat
const BASE_OFFSET_PX = 14 // constant lift in 2.5D so the lowest point still has visible wall thickness
const PERSPECTIVE_FACTOR = 0.22 // back of canvas X-compression in 2.5D — 0 = pure dimetric, 0.5 = strong perspective

// Presentation rotation (tracks.map_rotation_deg): returns a function that rotates
// a world (x, z) point around the pivot. The session map's z-axis runs opposite to
// the admin hero map's (no vertical flip), so the angle is negated here to make a
// given map_rotation_deg rotate the SAME visual direction in both. Applied inside
// the projector so every render site (surface, racing line, corners, car dot) and
// the grid stay consistent.
function makeXZRotator(deg, cx, cz) {
  if (!deg) return (x, z) => [x, z]
  const rad = (-deg * Math.PI) / 180
  const cos = Math.cos(rad),
    sin = Math.sin(rad)
  return (x, z) => {
    const dx = x - cx,
      dz = z - cz
    return [cx + dx * cos - dz * sin, cz + dx * sin + dz * cos]
  }
}

function loadLayers() {
  try {
    const raw = sessionStorage.getItem(LAYERS_STORAGE_KEY)
    if (raw) {
      const layers = { ...DEFAULT_LAYERS, ...JSON.parse(raw) }
      // Migrate old "flags" key to "events"
      if ("flags" in layers && !("events" in layers)) {
        layers.events = layers.flags
      }
      delete layers.flags
      return layers
    }
  } catch (_) {
    /* ignore */
  }
  return { ...DEFAULT_LAYERS }
}

function saveLayers(layers) {
  try {
    sessionStorage.setItem(LAYERS_STORAGE_KEY, JSON.stringify(layers))
  } catch (_) {
    /* ignore */
  }
}

function loadMinimapHidden() {
  return sessionStorage.getItem(MINIMAP_STORAGE_KEY) === "true"
}

function saveMinimapHidden(hidden) {
  try {
    sessionStorage.setItem(MINIMAP_STORAGE_KEY, String(hidden))
  } catch (_) {
    /* ignore */
  }
}

function loadViewMode() {
  try {
    const v = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    if (v === VIEW_MODE_25D) return VIEW_MODE_25D
  } catch (_) {
    /* ignore */
  }
  return VIEW_MODE_2D
}

function saveViewMode(mode) {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  } catch (_) {
    /* ignore */
  }
}

// Robust min/max from a sorted array using percentiles to ignore outliers.
function percentileExtent(values, lo = 0.05, hi = 0.95) {
  if (!values || values.length === 0) return [0, 0]
  const sorted = [...values].sort((a, b) => a - b)
  const loIdx = Math.max(0, Math.floor(sorted.length * lo))
  const hiIdx = Math.min(sorted.length - 1, Math.ceil(sorted.length * hi))
  return [sorted[loIdx], sorted[hiIdx]]
}

export default class extends Controller {
  static targets = ["canvas", "minimap"]
  static values = {
    telemetry: Array,
    comparison: { type: Boolean, default: false },
    laps: Array,
    teamId: Number,
    driverName: String,
    sector2Distance: Number,
    sector3Distance: Number,
    sectorStarts: Array,
    centerLine: Array,
    racingLine: Array,
    corners: Array,
    curvature: Array,
    events: { type: Array, default: [] },
    raceEvents: { type: Array, default: [] },
    cornerAnalysis: { type: Array, default: [] },
    rotation: { type: Number, default: 0 },
  }

  connect() {
    this.telemetry = null
    this.comparisonLaps = null
    this._currentZoomK = 1
    this._currentTransform = d3.zoomIdentity
    this._positionData = null
    this._svgWidth = 0
    this._svgHeight = 0
    this._visibleLayers = loadLayers()
    this._minimapHidden = loadMinimapHidden()
    this._viewMode = loadViewMode()

    // Canvas state
    this._trackCanvas = null
    this._trackCtx = null
    this._dpr = 1

    // Replay tracking state
    this._replayActive = false

    this.handleLoaded = (e) => this._onTelemetryLoaded(e)
    this.handleSyncHover = (e) => this.onHover(e)
    this.handleSyncLeave = () => this.onLeave()
    this.handleSyncZoom = (e) => this._onSyncZoom(e)
    this.handlePitWallUpdate = (e) => this._onPitWallUpdate(e)

    this.element.addEventListener("telemetry:loaded", this.handleLoaded)
    this.element.addEventListener("sync:hover", this.handleSyncHover)
    this.element.addEventListener("sync:leave", this.handleSyncLeave)
    this.element.addEventListener("sync:zoom", this.handleSyncZoom)
    window.addEventListener("pit-wall:update", this.handlePitWallUpdate)

    this._lastCanvasWidth = 0
    this._lastCanvasHeight = 0
    this.resizeObserver = new ResizeObserver((entries) => {
      if (!this.telemetry && !this.comparisonLaps) return
      const entry = entries[0]
      const w = entry.contentRect.width
      const h = entry.contentRect.height
      if (w === 0 || h === 0) return
      if (w === this._lastCanvasWidth && h === this._lastCanvasHeight) return
      this._lastCanvasWidth = w
      this._lastCanvasHeight = h
      this._scheduleRender()
    })
    this.resizeObserver.observe(this.canvasTarget)

    this._onAppearanceChange = () => {
      if (this.telemetry || this.comparisonLaps) this._scheduleRender()
    }
    window.addEventListener("appearance:change", this._onAppearanceChange)

    this._showLoading()
  }

  disconnect() {
    if (this._renderRAF) cancelAnimationFrame(this._renderRAF)
    if (this._redrawRAF) cancelAnimationFrame(this._redrawRAF)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    this.element.removeEventListener("telemetry:loaded", this.handleLoaded)
    this.element.removeEventListener("sync:hover", this.handleSyncHover)
    this.element.removeEventListener("sync:leave", this.handleSyncLeave)
    this.element.removeEventListener("sync:zoom", this.handleSyncZoom)
    window.removeEventListener("pit-wall:update", this.handlePitWallUpdate)
    window.removeEventListener("appearance:change", this._onAppearanceChange)
  }

  // --- Stimulus actions ---

  zoomIn() {
    if (!this._svg || !this.zoom) return
    this._svg.transition().duration(200).call(this.zoom.scaleBy, 1.5)
  }

  zoomOut() {
    if (!this._svg || !this.zoom) return
    this._svg
      .transition()
      .duration(200)
      .call(this.zoom.scaleBy, 1 / 1.5)
  }

  resetZoom() {
    if (!this._svg || !this.zoom) return
    this._svg
      .transition()
      .duration(300)
      .call(this.zoom.transform, d3.zoomIdentity)
    this._clearZoom()
    this.element.dispatchEvent(
      new CustomEvent("telemetry:zoom", {
        bubbles: true,
        detail: { distanceRange: null, source: this.element },
      }),
    )
  }

  toggleMinimap() {
    if (!this.hasMinimapTarget) return
    this._minimapHidden = !this._minimapHidden
    this._applyMinimapState()
    saveMinimapHidden(this._minimapHidden)
  }

  _applyMinimapState() {
    if (!this.hasMinimapTarget) return
    const el = this.minimapTarget
    if (this._minimapHidden) {
      el.style.maxHeight = "0px"
      el.style.opacity = "0"
      el.style.marginTop = "0"
    } else {
      el.style.maxHeight = `${this._minimapHeight}px`
      el.style.opacity = "1"
      el.style.marginTop = ""
    }
  }

  // Telemetry is the source of truth for altitude — the centerline's stored
  // altitudes can be in a different reference frame (computed from a different
  // game's data, off by absolute offset, etc.). _renderSingle / _renderComparison
  // override the centerline and racing-line altitudes with telemetry-derived
  // ones via nearest-neighbor (x, z) lookup so all geometry projects from one
  // consistent altitude source.
  _telemetryPoint(d) {
    return this._project(d.pos_x, d.pos_y || 0, d.pos_z)
  }

  // Build a corrected copy of an array of [x, y, z, ...rest] points where each
  // y is replaced with the pos_y of the nearest telemetry sample by (x, z).
  // Telemetry is assumed roughly ordered along the lap, so we use a sliding
  // window to keep the search bounded. Returns the original array if there is
  // no usable telemetry.
  _correctAltitudes(points, telemetry) {
    if (!points || points.length === 0) return points
    if (!telemetry || telemetry.length < 10) return points

    // Cache by (points, telemetry) reference. Resize re-renders pass the same
    // arrays, so the ~57 ms nearest-neighbor scan becomes a free WeakMap hit.
    if (!this._altitudeCache) this._altitudeCache = new WeakMap()
    const cached = this._altitudeCache.get(points)
    if (cached && cached.telemetry === telemetry) return cached.result

    const corrected = new Array(points.length)
    let j = 0
    const winRadius = 200
    for (let i = 0; i < points.length; i++) {
      const p = points[i]
      const x = p[0],
        z = p[2]
      const winStart = Math.max(0, j - winRadius)
      const winEnd = Math.min(telemetry.length - 1, j + winRadius)
      let bestJ = winStart,
        bestD = Infinity
      for (let k = winStart; k <= winEnd; k++) {
        const t = telemetry[k]
        const dx = t.pos_x - x,
          dz = t.pos_z - z
        const d2 = dx * dx + dz * dz
        if (d2 < bestD) {
          bestD = d2
          bestJ = k
        }
      }
      const y = telemetry[bestJ].pos_y
      const np = p.slice()
      np[1] = y != null ? y : p[1] || 0
      corrected[i] = np
      j = bestJ
    }
    this._altitudeCache.set(points, { telemetry, result: corrected })
    return corrected
  }

  toggleViewMode(e) {
    const mode =
      e.params.viewMode === VIEW_MODE_25D ? VIEW_MODE_25D : VIEW_MODE_2D
    if (mode === this._viewMode) return
    this._viewMode = mode
    saveViewMode(mode)
    if (this.telemetry || this.comparisonLaps) this._scheduleRender()
  }

  toggleLayer(e) {
    const layer = e.params.layer
    const el = e.currentTarget
    const nowVisible =
      el.type === "checkbox" ? el.checked : !(el.dataset.active === "true")

    if (el.type !== "checkbox") {
      el.dataset.active = nowVisible ? "true" : "false"
      if (nowVisible) {
        el.classList.remove("bg-app-surface-alt", "text-app-text-muted")
        el.classList.add("bg-vivid-purple", "text-white")
      } else {
        el.classList.remove("bg-vivid-purple", "text-white")
        el.classList.add("bg-app-surface-alt", "text-app-text-muted")
      }
    }

    this._visibleLayers[layer] = nowVisible
    saveLayers(this._visibleLayers)

    if (layer === "sectors") {
      if (this._sectorGroup) {
        this._sectorGroup.style("display", nowVisible ? null : "none")
      }
    } else if (layer === "events") {
      if (this._eventGroup) {
        this._eventGroup.style("display", nowVisible ? null : "none")
      }
      this._scheduleRedraw()
    } else {
      this._scheduleRedraw()
    }
  }

  _onSyncZoom(event) {
    const { distanceRange } = event.detail
    if (!this._positionData || !this._svg || !this.zoom) return

    if (!distanceRange) {
      this._syncZoomTarget = d3.zoomIdentity
      if (!this._syncZoomRAF)
        this._syncZoomRAF = requestAnimationFrame(() => this._tickSyncZoom())
      return
    }

    const [dMin, dMax] = distanceRange
    const visible = this._positionData.filter(
      (d) => d.distance_m >= dMin && d.distance_m <= dMax,
    )
    if (visible.length < 2) return

    let svgXMin = Infinity,
      svgXMax = -Infinity
    let svgYMin = Infinity,
      svgYMax = -Infinity
    visible.forEach((d) => {
      const [sx, sy] = this._telemetryPoint(d)
      if (sx < svgXMin) svgXMin = sx
      if (sx > svgXMax) svgXMax = sx
      if (sy < svgYMin) svgYMin = sy
      if (sy > svgYMax) svgYMax = sy
    })

    const bboxW = svgXMax - svgXMin || 1
    const bboxH = svgYMax - svgYMin || 1
    const bboxCX = (svgXMin + svgXMax) / 2
    const bboxCY = (svgYMin + svgYMax) / 2

    const w = this._svgWidth
    const h = this._svgHeight
    const padding = 0.85
    const k = Math.min(w / bboxW, h / bboxH) * padding
    const tx = w / 2 - bboxCX * k
    const ty = h / 2 - bboxCY * k

    this._syncZoomTarget = d3.zoomIdentity.translate(tx, ty).scale(k)
    if (!this._syncZoomRAF)
      this._syncZoomRAF = requestAnimationFrame(() => this._tickSyncZoom())
  }

  _tickSyncZoom() {
    this._syncZoomRAF = null
    const target = this._syncZoomTarget
    if (!target || !this._svg) return

    // Cancel any wheel-driven lerp so they don't fight
    if (this._zoomRAF) {
      cancelAnimationFrame(this._zoomRAF)
      this._zoomRAF = null
      this._zoomTarget = null
    }

    const cur = this._currentTransform || d3.zoomIdentity
    const lerpFactor = 0.15

    const k = cur.k + (target.k - cur.k) * lerpFactor
    const x = cur.x + (target.x - cur.x) * lerpFactor
    const y = cur.y + (target.y - cur.y) * lerpFactor

    if (
      Math.abs(k - target.k) < 0.001 &&
      Math.abs(x - target.x) < 0.1 &&
      Math.abs(y - target.y) < 0.1
    ) {
      this._svg.call(this.zoom.transform, target)
      this._syncZoomTarget = null
      return
    }

    this._svg.call(
      this.zoom.transform,
      d3.zoomIdentity.translate(x, y).scale(k),
    )
    this._syncZoomRAF = requestAnimationFrame(() => this._tickSyncZoom())
  }

  // --- Internal ---

  _showLoading() {
    this.canvasTarget.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">Loading telemetry&hellip;</div>'
  }

  _onTelemetryLoaded(event) {
    const { telemetry, laps, zoomState } = event.detail

    if (laps && laps.length > 1) {
      this.comparisonLaps = laps
      this.telemetry = telemetry
      this._isComparison = true
    } else {
      this.telemetry = telemetry
      this.comparisonLaps = null
      this._isComparison = false
    }

    this._replayActive = false
    this.render()

    // Restore persisted zoom state after rendering
    if (zoomState) {
      this._onSyncZoom({ detail: { distanceRange: zoomState } })
    }
  }

  render() {
    const container = this.canvasTarget
    container.innerHTML = ""
    this._currentTransform = d3.zoomIdentity
    this._currentZoomK = 1

    if (
      (this.comparisonValue || this._isComparison) &&
      (this.comparisonLaps || this.hasLapsValue)
    ) {
      this._renderComparison(container)
    } else {
      this._renderSingle(container)
    }

    this._syncLayerControls()
  }

  _syncLayerControls() {
    // Sync checkbox inputs (lap detail view)
    this.element
      .querySelectorAll('input[type="checkbox"][data-track-map-layer-param]')
      .forEach((el) => {
        const layer = el.dataset.trackMapLayerParam
        if (layer in this._visibleLayers) {
          el.checked = this._visibleLayers[layer]
        }
      })

    // Sync button toggles (comparison view)
    this.element
      .querySelectorAll("button[data-track-map-layer-param]")
      .forEach((el) => {
        const layer = el.dataset.trackMapLayerParam
        if (!(layer in this._visibleLayers)) return
        const active = this._visibleLayers[layer]
        el.dataset.active = active ? "true" : "false"
        if (active) {
          el.classList.remove("bg-app-surface-alt", "text-app-text-muted")
          el.classList.add("bg-vivid-purple", "text-white")
        } else {
          el.classList.remove("bg-vivid-purple", "text-white")
          el.classList.add("bg-app-surface-alt", "text-app-text-muted")
        }
      })

    // Sync 2D / 2.5D segmented control buttons
    this.element
      .querySelectorAll("button[data-track-map-view-mode-param]")
      .forEach((el) => {
        const mode = el.dataset.trackMapViewModeParam
        const active = mode === this._viewMode
        el.dataset.active = active ? "true" : "false"
        if (active) {
          el.classList.remove("text-white/60", "hover:text-white")
          el.classList.add("bg-white/10", "text-white")
        } else {
          el.classList.remove("bg-white/10", "text-white")
          el.classList.add("text-white/60", "hover:text-white")
        }
      })
  }

  _computeScales(data, width, height, padding, opts = {}) {
    // Presentation rotation (tracks.map_rotation_deg): rotate every point around
    // the data centroid before fitting bounds, so the rotated track re-fits the
    // viewport. The pivot is in RAW world coords; `rot` is baked into
    // project()/groundProject() below so all render sites rotate for free.
    const rotationDeg = this.hasRotationValue ? this.rotationValue : 0
    const rawX = d3.extent(data, (d) => d.pos_x)
    const rawZ = d3.extent(data, (d) => d.pos_z)
    const rot = makeXZRotator(
      rotationDeg,
      (rawX[0] + rawX[1]) / 2,
      (rawZ[0] + rawZ[1]) / 2,
    )

    const rotated = rotationDeg ? data.map((d) => rot(d.pos_x, d.pos_z)) : null
    const xValues = rotated
      ? rotated.map((p) => p[0])
      : data.map((d) => d.pos_x)
    const zValues = rotated
      ? rotated.map((p) => p[1])
      : data.map((d) => d.pos_z)
    const xExtent = d3.extent(xValues)
    const zExtent = d3.extent(zValues)

    const mode = opts.mode || VIEW_MODE_2D
    const altitudes =
      opts.altitudes && opts.altitudes.length > 0 ? opts.altitudes : null
    const yExtent = altitudes ? percentileExtent(altitudes, 0.05, 0.95) : [0, 0]
    const yMin = yExtent[0]
    const yRange = yExtent[1] - yExtent[0]
    const has25D = mode === VIEW_MODE_25D && yRange >= FLAT_TRACK_THRESHOLD_M

    const tiltK = has25D ? TILT_K : 1
    const dataWidth = xExtent[1] - xExtent[0] || 1
    const dataHeight = zExtent[1] - zExtent[0] || 1

    const availableWidth = width - padding * 2
    const availableHeight = height - padding * 2
    const padFactor = 0.85

    // Absolute altitude lift: a constant LIFT_PER_METER, capped so very high
    // ranges don't dominate the viewport. Big-elevation tracks (Spa, Spielberg)
    // get proportionally more lift than low-elevation tracks (COTA, Bahrain).
    const desiredLiftPx = has25D ? LIFT_PER_METER * yRange : 0
    const maxLiftPx = availableHeight * MAX_LIFT_FRACTION
    const altitudeLiftPx = Math.min(desiredLiftPx, maxLiftPx)
    const totalReservedPx = has25D ? altitudeLiftPx + BASE_OFFSET_PX : 0

    // Fit the dimetric ground-plane data within the canvas after reserving
    // vertical room for the altitude lift + base offset.
    const widthScale = (availableWidth * padFactor) / dataWidth
    const heightScale =
      (availableHeight * padFactor - totalReservedPx) / (dataHeight * tiltK)
    const scale = Math.max(0.001, Math.min(widthScale, heightScale))

    const visibleHeight = dataHeight * scale * tiltK + totalReservedPx
    const offsetX = padding + (availableWidth - dataWidth * scale) / 2
    const offsetY =
      padding + (availableHeight - visibleHeight) / 2 + totalReservedPx

    // Effective px/meter — usually LIFT_PER_METER, less when the cap kicked in.
    const vScale = has25D && yRange > 0 ? altitudeLiftPx / yRange : 0

    const xScale = (v) => offsetX + (v - xExtent[0]) * scale
    const zScale = (v) => offsetY + (v - zExtent[0]) * scale * tiltK

    // Lift in pixels for a given world altitude — pure altitude term, no base.
    const altitudeLift = has25D ? (y) => vScale * ((y || 0) - yMin) : () => 0

    // Horizontal foreshortening: scale X around the centerline by a factor that
    // shrinks with depth. The dimetric tilt sends low-z to the top of the
    // canvas (= far from the viewer) and high-z to the bottom (= near), so we
    // compress at low z and leave high z at full width. Vertical world-z lines
    // then slant toward a central vanishing point at the top of the canvas,
    // matching the trapezoidal feel of a fixed perspective camera.
    const xMidScreen = xScale((xExtent[0] + xExtent[1]) / 2)
    const zSpan = Math.max(1, zExtent[1] - zExtent[0])
    const compress = has25D
      ? (z) => 1 - (PERSPECTIVE_FACTOR * (zExtent[1] - z)) / zSpan
      : () => 1
    const perspectiveX = (x, z) =>
      xMidScreen + (xScale(x) - xMidScreen) * compress(z)

    // `project` is used by track surface, telemetry, racing line, walls (top).
    // It includes BASE_OFFSET_PX so the track always sits above the floor in
    // 2.5D, giving the lowest point visible wall thickness.
    // Scale-only projection — operates in the (already-rotated) extent space.
    // The grid uses this directly (its inverses also live in extent space), so the
    // grid stays screen-aligned regardless of rotation.
    const projectScaled = has25D
      ? (x, y, z) => [
          perspectiveX(x, z),
          zScale(z) - altitudeLift(y) - BASE_OFFSET_PX,
        ]
      : (x, _y, z) => [xScale(x), zScale(z)]
    const gridProject = has25D
      ? (x, z) => [perspectiveX(x, z), zScale(z)]
      : (x, z) => [xScale(x), zScale(z)]

    // Public projection — takes RAW world coords, rotates them, then scales, so
    // every render site (surface, racing line, corners, telemetry dot) is oriented
    // with no per-site change.
    const project = (x, y, z) => {
      const [rx, rz] = rot(x, z)
      return projectScaled(rx, y, rz)
    }
    const groundProject = (x, z) => {
      const [rx, rz] = rot(x, z)
      return gridProject(rx, rz)
    }

    // Inverses — used to compute the visible world rectangle from a screen-space
    // viewport so the grid can render only what's on screen at any zoom level.
    const xScaleInv = (sx) =>
      scale > 0 ? (sx - offsetX) / scale + xExtent[0] : 0
    const zScaleInv = (sy) =>
      scale * tiltK > 0 ? (sy - offsetY) / (scale * tiltK) + zExtent[0] : 0
    const perspectiveXInv = has25D
      ? (sx, z) => {
          const c = Math.max(0.001, compress(z))
          return xScaleInv((sx - xMidScreen) / c + xMidScreen)
        }
      : (sx, _z) => xScaleInv(sx)

    return {
      xScale,
      zScale,
      project,
      groundProject,
      gridProject,
      xScaleInv,
      zScaleInv,
      perspectiveXInv,
      xExtent,
      zExtent,
      yExtent,
      vScale,
      tiltK,
      mode,
      has25D,
      yMin,
    }
  }

  // --- Layout: Canvas (track data) + SVG (overlays + zoom interaction) ---

  _createLayout(container, width, height) {
    const wrapper = document.createElement("div")
    wrapper.style.cssText = "position:relative;width:100%;height:100%"

    const dpr = window.devicePixelRatio || 1
    const canvas = document.createElement("canvas")
    canvas.style.cssText = `position:absolute;top:0;left:0;width:${width}px;height:${height}px`
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
    const ctx = canvas.getContext("2d")
    ctx.scale(dpr, dpr)
    wrapper.appendChild(canvas)

    const svg = d3
      .select(wrapper)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
      .style("cursor", "grab")

    const overlayGroup = svg.append("g").attr("class", "overlay-group")

    this._sectorGroup = overlayGroup
      .append("g")
      .attr("class", "layer-sectors")
      .style("display", this._visibleLayers.sectors ? null : "none")
    this._eventGroup = overlayGroup
      .append("g")
      .attr("class", "layer-events")
      .style("display", this._visibleLayers.events ? null : "none")
    this._cornerLabelGroup = overlayGroup
      .append("g")
      .attr("class", "layer-corner-labels")
    this._drsLabelGroup = overlayGroup
      .append("g")
      .attr("class", "layer-drs-labels")
    this._carMarkerGroup = overlayGroup
      .append("g")
      .attr("class", "layer-car-marker")

    // Minimap canvas (144×96 CSS px, 3:2 aspect ratio) — created once
    if (!this._minimapCanvas) {
      const mmW = 144
      const mmH = 96
      const mmCanvas = document.createElement("canvas")
      mmCanvas.style.cssText = `width:${mmW}px;height:${mmH}px;pointer-events:none;display:block`
      mmCanvas.width = Math.round(mmW * dpr)
      mmCanvas.height = Math.round(mmH * dpr)
      const mmCtx = mmCanvas.getContext("2d")
      mmCtx.scale(dpr, dpr)
      if (this.hasMinimapTarget) {
        this.minimapTarget.appendChild(mmCanvas)
        this._minimapHeight = mmH
        this._applyMinimapState()
      } else {
        mmCanvas.style.cssText = `position:absolute;top:8px;left:8px;width:${mmW}px;height:${mmH}px;pointer-events:none;z-index:1`
        wrapper.appendChild(mmCanvas)
      }
      this._minimapCanvas = mmCanvas
      this._minimapCtx = mmCtx
      this._minimapWidth = mmW
      this._minimapHeight = mmH
    }

    container.appendChild(wrapper)

    this._trackCanvas = canvas
    this._trackCtx = ctx
    this._dpr = dpr
    this._svg = svg
    this._overlayGroup = overlayGroup

    this.zoom = d3
      .zoom()
      .scaleExtent([0.5, 20])
      .filter((event) => {
        if (event.type === "wheel") return false
        return !event.ctrlKey && !event.button
      })
      .on("zoom", (event) => {
        // Cancel any running smooth-zoom animations so they don't fight with drag
        if (this._zoomRAF) {
          cancelAnimationFrame(this._zoomRAF)
          this._zoomRAF = null
          this._zoomTarget = null
        }
        if (this._syncZoomRAF) {
          cancelAnimationFrame(this._syncZoomRAF)
          this._syncZoomRAF = null
          this._syncZoomTarget = null
        }
        this._currentTransform = event.transform
        this._currentZoomK = event.transform.k
        overlayGroup.attr("transform", event.transform)
        this._scheduleRedraw()
        this._compensateOverlay(event.transform.k)
        this._saveZoom(event.transform)
      })
    svg.call(this.zoom)

    // Smooth wheel zoom — lerp toward target each frame
    this._zoomTarget = null
    const zoomScaleExtent = [0.5, 20]
    const lerpFactor = 0.25

    const tickZoom = () => {
      this._zoomRAF = null
      const target = this._zoomTarget
      if (!target) return
      const cur = this._currentTransform || d3.zoomIdentity

      const k = cur.k + (target.k - cur.k) * lerpFactor
      const x = cur.x + (target.x - cur.x) * lerpFactor
      const y = cur.y + (target.y - cur.y) * lerpFactor

      // Stop when close enough
      if (
        Math.abs(k - target.k) < 0.001 &&
        Math.abs(x - target.x) < 0.1 &&
        Math.abs(y - target.y) < 0.1
      ) {
        svg.call(this.zoom.transform, target)
        this._zoomTarget = null
        return
      }

      svg.call(this.zoom.transform, d3.zoomIdentity.translate(x, y).scale(k))
      this._zoomRAF = requestAnimationFrame(tickZoom)
    }

    svg.on(
      "wheel.smoothZoom",
      (event) => {
        event.preventDefault()
        // Cancel any sync zoom animation so they don't fight
        if (this._syncZoomRAF) {
          cancelAnimationFrame(this._syncZoomRAF)
          this._syncZoomRAF = null
          this._syncZoomTarget = null
        }
        const delta =
          -event.deltaY *
          (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002)
        const t = this._zoomTarget || this._currentTransform || d3.zoomIdentity
        const newK = Math.max(
          zoomScaleExtent[0],
          Math.min(zoomScaleExtent[1], t.k * Math.pow(2, delta)),
        )

        const [mx, my] = d3.pointer(event, svg.node())
        const newX = mx - (mx - t.x) * (newK / t.k)
        const newY = my - (my - t.y) * (newK / t.k)
        this._zoomTarget = d3.zoomIdentity.translate(newX, newY).scale(newK)

        if (!this._zoomRAF) this._zoomRAF = requestAnimationFrame(tickZoom)
      },
      { passive: false },
    )

    // Restore saved zoom transform from previous lap view
    const savedZoom = this._loadZoom()
    if (savedZoom) {
      const t = d3.zoomIdentity
        .translate(savedZoom.x, savedZoom.y)
        .scale(savedZoom.k)
      svg.call(this.zoom.transform, t)
    }

    return { svg, overlayGroup }
  }

  // --- Single-lap rendering ---

  _renderSingle(container) {
    let data = this.telemetry
    if (!data || data.length === 0) {
      container.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">No telemetry data</div>'
      return
    }

    // Strip trailing zero-position frames (F1 game lap-boundary artifact, may persist in cache)
    while (
      data.length > 1 &&
      data[data.length - 1].pos_x === 0 &&
      data[data.length - 1].pos_z === 0
    ) {
      data = data.slice(0, -1)
    }

    if (data.length < 2) return

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const padding = 0
    const rawCl = this.hasCenterLineValue ? this.centerLineValue : null
    const rawRl = this.hasRacingLineValue ? this.racingLineValue : null
    const cl =
      rawCl && rawCl.length >= 2 ? this._correctAltitudes(rawCl, data) : null
    this._activeCenterLine = cl
    this._activeRacingLine = rawRl ? this._correctAltitudes(rawRl, data) : null

    // Compute scales from active data (all in same coordinate frame)
    let scaleData = data
    if (cl && cl.length >= 3) {
      let maxW = 0
      const clPoints = cl.map(([x, _y, z, wL, wR]) => {
        if (wL > maxW) maxW = wL
        if (wR > maxW) maxW = wR
        return { pos_x: x, pos_z: z }
      })
      const clXs = cl.map((p) => p[0]),
        clZs = cl.map((p) => p[2])
      const clXMin = Math.min(...clXs),
        clXMax = Math.max(...clXs)
      const clZMin = Math.min(...clZs),
        clZMax = Math.max(...clZs)
      clPoints.push({ pos_x: clXMin - maxW, pos_z: clZMin - maxW })
      clPoints.push({ pos_x: clXMax + maxW, pos_z: clZMax + maxW })
      scaleData = data.concat(clPoints)
    }

    // Altitudes for 2.5D: prefer centerline (covers the full lap even when
    // telemetry has been trimmed), fall back to telemetry pos_y.
    const altitudes =
      cl && cl.length >= 3
        ? cl.map((p) => p[1] || 0)
        : data.map((d) => d.pos_y || 0)

    const scales = this._computeScales(scaleData, width, height, padding, {
      mode: this._viewMode,
      altitudes,
    })
    const {
      xScale,
      zScale,
      project,
      groundProject,
      gridProject,
      xScaleInv,
      zScaleInv,
      perspectiveXInv,
      xExtent,
      zExtent,
      has25D,
      yMin,
      vScale,
    } = scales
    this._xScale = xScale
    this._zScale = zScale
    this._project = project
    this._groundProject = groundProject
    this._gridProject = gridProject
    this._xScaleInv = xScaleInv
    this._zScaleInv = zScaleInv
    this._perspectiveXInv = perspectiveXInv
    this._has25D = has25D
    this._yMin = yMin
    this._vScale = vScale
    this._svgWidth = width
    this._svgHeight = height
    this._positionData = data

    const speedExtent = d3.extent(data, (d) => d.speed_kmh)

    this._colorScale = d3
      .scaleSequential(d3.interpolateRdYlGn)
      .domain([speedExtent[1], speedExtent[0]])
    this._brakeColorScale = d3
      .scaleSequential(d3.interpolateRgb("#fca5a5", "#ff0000"))
      .domain([0, 1])
    this._throttleColorScale = d3
      .scaleSequential(d3.interpolateRgb("#bbf7d0", "#00cc00"))
      .domain([0, 1])
    this._renderData = data
    this._simplifiedIndices = rdpSimplify(data)
    this._stride = Math.max(1, Math.floor(data.length / 2000))
    this._xExtent = xExtent
    this._zExtent = zExtent
    this._isComparison = false

    this._createLayout(container, width, height)
    this._redrawCanvas()

    // SVG overlays (few elements)
    this._detectTelemetryEvents(data)
    this._renderEventLabels(this._eventGroup, data, project)
    this._renderSectorMarkers(this._sectorGroup, data, project)
    this._renderCornerLabels(this._cornerLabelGroup, project)
    this._renderDrsLabels(this._drsLabelGroup, data, project)
    this._setupCarMarker(this._carMarkerGroup)

    // Compensate overlays for restored zoom (they were created after zoom restore)
    if (this._currentZoomK !== 1) this._compensateOverlay(this._currentZoomK)

    this._drawMinimap()
    this._trackData = data
  }

  // --- Comparison rendering ---

  _renderComparison(container) {
    const laps = this.comparisonLaps || this.lapsValue
    if (!laps || laps.length === 0) {
      container.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">No telemetry data</div>'
      return
    }

    const rawCl = this.hasCenterLineValue ? this.centerLineValue : null
    const rawRl = this.hasRacingLineValue ? this.racingLineValue : null

    const filteredLaps = laps.map((lap) => {
      let tel = lap.telemetry || []
      // Strip trailing zero-position frames (F1 game lap-boundary artifact)
      while (
        tel.length > 1 &&
        tel[tel.length - 1].pos_x === 0 &&
        tel[tel.length - 1].pos_z === 0
      ) {
        tel = tel.slice(0, -1)
      }
      return { ...lap, telemetry: tel }
    })

    // Use the primary (first) lap's telemetry as the altitude source for the
    // shared track geometry — both laps run the same physical track so the
    // resulting altitudes are interchangeable for projection purposes.
    const altSource = filteredLaps[0]?.telemetry || []
    const cl =
      rawCl && rawCl.length >= 2
        ? this._correctAltitudes(rawCl, altSource)
        : null
    this._activeCenterLine = cl
    this._activeRacingLine = rawRl
      ? this._correctAltitudes(rawRl, altSource)
      : null

    let allX = []
    let allZ = []
    filteredLaps.forEach((lap) => {
      lap.telemetry.forEach((t) => {
        allX.push(t.pos_x)
        allZ.push(t.pos_z)
      })
    })

    if (allX.length === 0) {
      container.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">No telemetry data</div>'
      return
    }

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const padding = 0

    if (cl && cl.length >= 3) {
      let maxW = 0
      for (const [x, _y, z, wL, wR] of cl) {
        allX.push(x)
        allZ.push(z)
        if (wL > maxW) maxW = wL
        if (wR > maxW) maxW = wR
      }
      const clXMin = Math.min(...cl.map((p) => p[0]))
      const clXMax = Math.max(...cl.map((p) => p[0]))
      const clZMin = Math.min(...cl.map((p) => p[2]))
      const clZMax = Math.max(...cl.map((p) => p[2]))
      allX.push(clXMin - maxW, clXMax + maxW)
      allZ.push(clZMin - maxW, clZMax + maxW)
    }

    // Altitudes for 2.5D: prefer centerline; fall back to merged telemetry pos_y.
    let altitudes
    if (cl && cl.length >= 3) {
      altitudes = cl.map((p) => p[1] || 0)
    } else {
      altitudes = []
      filteredLaps.forEach((lap) =>
        lap.telemetry.forEach((t) => altitudes.push(t.pos_y || 0)),
      )
    }

    const scaleData = allX.map((x, i) => ({ pos_x: x, pos_z: allZ[i] }))
    const scales = this._computeScales(scaleData, width, height, padding, {
      mode: this._viewMode,
      altitudes,
    })
    const {
      xScale,
      zScale,
      project,
      groundProject,
      gridProject,
      xScaleInv,
      zScaleInv,
      perspectiveXInv,
      xExtent,
      zExtent,
      has25D,
      yMin,
      vScale,
    } = scales
    this._xScale = xScale
    this._zScale = zScale
    this._project = project
    this._groundProject = groundProject
    this._gridProject = gridProject
    this._xScaleInv = xScaleInv
    this._zScaleInv = zScaleInv
    this._perspectiveXInv = perspectiveXInv
    this._has25D = has25D
    this._yMin = yMin
    this._vScale = vScale
    this._svgWidth = width
    this._svgHeight = height
    this._positionData = filteredLaps[0]?.telemetry || []

    const firstLapData = filteredLaps[0]?.telemetry || []
    this._brakeColorScale = d3
      .scaleSequential(d3.interpolateRgb("#fca5a5", "#ff0000"))
      .domain([0, 1])
    this._throttleColorScale = d3
      .scaleSequential(d3.interpolateRgb("#bbf7d0", "#00cc00"))
      .domain([0, 1])
    this._renderData = firstLapData
    this._simplifiedIndices = rdpSimplify(firstLapData)
    this._stride = Math.max(1, Math.floor(firstLapData.length / 2000))
    this._xExtent = xExtent
    this._zExtent = zExtent
    this._isComparison = true
    this._comparisonData = filteredLaps

    this._createLayout(container, width, height)
    this._redrawCanvas()

    // SVG overlays
    this._detectTelemetryEvents(firstLapData)
    this._renderEventLabels(this._eventGroup, firstLapData, project)
    if (firstLapData.length > 0) {
      this._renderSectorMarkers(this._sectorGroup, firstLapData, project)
    }
    this._renderCornerLabels(this._cornerLabelGroup, project)
    this._renderDrsLabels(this._drsLabelGroup, firstLapData, project)
    this._setupComparisonCarMarkers(this._carMarkerGroup, filteredLaps)

    // Compensate overlays for restored zoom (they were created after zoom restore)
    if (this._currentZoomK !== 1) this._compensateOverlay(this._currentZoomK)

    this._drawMinimap()
    this._comparisonLapsData = filteredLaps
  }

  // --- Canvas drawing ---

  _redrawCanvas() {
    const ctx = this._trackCtx
    if (!ctx) return
    const dpr = this._dpr
    const w = this._trackCanvas.width / dpr
    const h = this._trackCanvas.height / dpr
    const { x, y, k } = this._currentTransform

    ctx.save()
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    ctx.translate(x, y)
    ctx.scale(k, k)

    if (this._isComparison) {
      this._drawComparisonCanvas(ctx, k)
    } else {
      this._drawSingleCanvas(ctx, k)
    }

    ctx.restore()
  }

  _drawSingleCanvas(ctx, k) {
    const data = this._renderData
    if (!data || data.length < 2) return
    const indices = this._simplifiedIndices

    // Grid (always at ground plane)
    this._drawGrid(ctx, k)

    // Altitude wall (no-op in 2D)
    this._drawAltitudeWall(ctx, k)

    // Track surface (from pre-loaded center line)
    this._drawTrackSurface(ctx, k)

    // DRS zones (full-width yellow overlay on track surface)
    this._drawDrsZones(ctx, data)

    // Corner markers
    this._drawCornerMarkers(ctx, k)

    // Racing line (dashed)
    this._drawRacingLine(ctx, k)

    // Flag zone overlays (all flag colors)
    if (this._visibleLayers.events) {
      this._drawFlagOverlays(ctx, k, data)
    }

    // Speed-colored track segments
    if (this._visibleLayers.speed) {
      ctx.lineWidth = 3 / k
      ctx.lineCap = "round"
      if (USE_SPLINE) {
        this._drawColoredSpline(ctx, data, indices, (d) =>
          this._colorScale(d.speed_kmh),
        )
      } else {
        this._drawSegments(ctx, data, (d) => this._colorScale(d.speed_kmh))
      }
    }

    // Brake overlay
    if (this._visibleLayers.braking) {
      ctx.lineWidth = 3 / k
      ctx.lineCap = "round"
      if (USE_SPLINE) {
        this._drawColoredSpline(
          ctx,
          data,
          indices,
          (d) => this._brakeColorScale(d.brake_pct),
          (d) => d.brake_pct > Number.EPSILON,
        )
      } else {
        this._drawSegments(
          ctx,
          data,
          (d) => this._brakeColorScale(d.brake_pct),
          (d) => d.brake_pct > Number.EPSILON,
        )
      }
    }

    // Throttle overlay
    if (this._visibleLayers.throttle) {
      ctx.lineWidth = 3 / k
      ctx.lineCap = "round"
      if (USE_SPLINE) {
        this._drawColoredSpline(
          ctx,
          data,
          indices,
          (d) => this._throttleColorScale(d.throttle_pct),
          (d) => d.throttle_pct > Number.EPSILON,
        )
      } else {
        this._drawSegments(
          ctx,
          data,
          (d) => this._throttleColorScale(d.throttle_pct),
          (d) => d.throttle_pct > Number.EPSILON,
        )
      }
    }
  }

  _drawSegments(ctx, data, colorFn, filterFn) {
    const stride = this._stride
    for (let i = 0; i < data.length - 1; i += stride) {
      if (filterFn && !filterFn(data[i])) continue
      const ni = Math.min(i + stride, data.length - 1)
      const a = data[i]
      const b = data[ni]
      const [ax, ay] = this._telemetryPoint(a)
      const [bx, by] = this._telemetryPoint(b)
      ctx.strokeStyle = colorFn(a)
      ctx.beginPath()
      ctx.moveTo(ax, ay)
      ctx.lineTo(bx, by)
      ctx.stroke()
    }
  }

  _drawComparisonCanvas(ctx, k) {
    const laps = this._comparisonData
    if (!laps || laps.length === 0) return
    const firstData = laps[0]?.telemetry || []

    // Grid (always at ground plane)
    this._drawGrid(ctx, k)

    // Altitude wall (no-op in 2D)
    this._drawAltitudeWall(ctx, k)

    // Track surface (from pre-loaded center line)
    this._drawTrackSurface(ctx, k)

    // DRS zones from first lap (full-width yellow overlay on track surface)
    if (firstData.length >= 2) {
      this._drawDrsZones(ctx, firstData)
    }

    // Corner markers
    this._drawCornerMarkers(ctx, k)

    // Racing line (dashed)
    this._drawRacingLine(ctx, k)

    // Flag zone overlays from first lap (all flag colors)
    if (this._visibleLayers.events && firstData.length >= 2) {
      this._drawFlagOverlays(ctx, k, firstData)
    }

    // Each lap as colored path
    laps.forEach((lap, lapIndex) => {
      const data = lap.telemetry
      if (!data || data.length < 2) return
      ctx.strokeStyle = lap.color
      ctx.lineWidth = 3 / k
      ctx.lineCap = "round"
      ctx.lineJoin = "round"
      if (lapIndex > 0) {
        ctx.setLineDash([8 / k, 5 / k])
        ctx.globalAlpha = 0.6
      } else {
        ctx.setLineDash([])
        ctx.globalAlpha = 0.9
      }
      ctx.beginPath()
      const lapPoint = (i) => this._telemetryPoint(data[i])
      if (USE_SPLINE) {
        const lapIndices = rdpSimplify(data)
        traceSplinePath(ctx, lapIndices, lapPoint)
      } else {
        const [x0, y0] = lapPoint(0)
        ctx.moveTo(x0, y0)
        for (let i = 1; i < data.length; i++) {
          const [px, py] = lapPoint(i)
          ctx.lineTo(px, py)
        }
      }
      ctx.stroke()
      ctx.globalAlpha = 1
      ctx.setLineDash([])
    })

    // Brake overlay from first lap
    if (this._visibleLayers.braking && firstData.length >= 2) {
      ctx.lineWidth = 3 / k
      ctx.lineCap = "round"
      if (USE_SPLINE) {
        const indices = this._simplifiedIndices
        this._drawColoredSpline(
          ctx,
          firstData,
          indices,
          (d) => this._brakeColorScale(d.brake_pct),
          (d) => d.brake_pct > Number.EPSILON,
        )
      } else {
        this._drawSegments(
          ctx,
          firstData,
          (d) => this._brakeColorScale(d.brake_pct),
          (d) => d.brake_pct > Number.EPSILON,
        )
      }
    }

    // Throttle overlay from first lap
    if (this._visibleLayers.throttle && firstData.length >= 2) {
      ctx.lineWidth = 3 / k
      ctx.lineCap = "round"
      if (USE_SPLINE) {
        const indices = this._simplifiedIndices
        this._drawColoredSpline(
          ctx,
          firstData,
          indices,
          (d) => this._throttleColorScale(d.throttle_pct),
          (d) => d.throttle_pct > Number.EPSILON,
        )
      } else {
        this._drawSegments(
          ctx,
          firstData,
          (d) => this._throttleColorScale(d.throttle_pct),
          (d) => d.throttle_pct > Number.EPSILON,
        )
      }
    }
  }

  /**
   * Draw per-segment colored spline through simplified points.
   * Each segment gets its own color from colorFn. Optional filterFn skips segments.
   */
  _drawColoredSpline(ctx, data, indices, colorFn, filterFn) {
    const point = (i) => this._telemetryPoint(data[i])

    for (let k = 0; k < indices.length - 1; k++) {
      const i1 = indices[k]
      const i2 = indices[k + 1]

      if (filterFn && !filterFn(data[i1])) continue

      const i0 = k > 0 ? indices[k - 1] : i1
      const i3 = k < indices.length - 2 ? indices[k + 2] : i2

      const pi1 = point(i1)
      const pi2 = point(i2)

      ctx.strokeStyle = colorFn(data[i1])
      ctx.beginPath()
      ctx.moveTo(pi1[0], pi1[1])

      if (i0 === i1 || i2 === i3) {
        ctx.lineTo(pi2[0], pi2[1])
      } else {
        const pi0 = point(i0)
        const pi3 = point(i3)
        const { cp1x, cp1y, cp2x, cp2y } = catmullRomToBezier(
          pi0[0],
          pi0[1],
          pi1[0],
          pi1[1],
          pi2[0],
          pi2[1],
          pi3[0],
          pi3[1],
        )
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, pi2[0], pi2[1])
      }
      ctx.stroke()
    }
  }

  // Cached track boundaries — invalidated when centerline or projection changes.
  // Shared by _drawAltitudeWall, _drawTrackSurface, and _drawDrsZones (via
  // this._trackBoundaries) so a single redraw doesn't recompute boundaries
  // multiple times. The underlying computeTrackBoundaries call is O(N²) on
  // long tracks (Spa: ~6300 points), so this is the largest perf win.
  _getTrackBoundaries() {
    const cl = this._activeCenterLine
    if (!cl || cl.length < 3) return null
    const cache = this._cachedBoundaries
    if (cache && cache.cl === cl && cache.project === this._project) {
      return cache.value
    }
    const value = computeTrackBoundaries(cl, this._project)
    this._cachedBoundaries = { cl, project: this._project, value }
    return value
  }

  // Cached projected wall geometry — top edge from boundary projection, bottom
  // edge from groundProject. ~4N projection calls per redraw becomes ~0 once
  // cached. Invalidates with the boundary cache.
  _getWallStrips() {
    if (!this._has25D) return null
    const cl = this._activeCenterLine
    if (!cl || cl.length < 3) return null
    const cache = this._cachedWalls
    if (cache && cache.cl === cl && cache.project === this._project) {
      return cache.value
    }
    const boundaries = this._getTrackBoundaries()
    if (!boundaries) return null
    const value = {
      left: this._buildWallStrip(boundaries.leftWorld),
      right: this._buildWallStrip(boundaries.rightWorld),
    }
    this._cachedWalls = { cl, project: this._project, value }
    return value
  }

  _buildWallStrip(worldPoints) {
    const project = this._project
    const groundProject = this._groundProject
    const top = new Array(worldPoints.length)
    const bottom = new Array(worldPoints.length)
    for (let i = 0; i < worldPoints.length; i++) {
      const [x, y, z] = worldPoints[i]
      top[i] = project(x, y, z)
      bottom[i] = groundProject(x, z)
    }
    return { top, bottom }
  }

  _drawAltitudeWall(ctx, _k) {
    const strips = this._getWallStrips()
    if (!strips) return

    this._drawWallStrip(ctx, strips.left)
    this._drawWallStrip(ctx, strips.right)
  }

  _drawWallStrip(ctx, strip) {
    const { top, bottom } = strip
    const n = top.length
    if (n < 2) return

    // Per-segment linear gradient — each quad fades 35% (track edge) → 10%
    // (ground) across its own height, so visual depth scales with altitude
    // instead of bleeding across the whole canvas.
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      const t1 = top[i],
        t2 = top[j]
      const b1 = bottom[i],
        b2 = bottom[j]

      const segTop = Math.min(t1[1], t2[1])
      const segBot = Math.max(b1[1], b2[1])
      if (segBot - segTop < 1) continue // skip near-zero-height quads

      const grad = ctx.createLinearGradient(0, segTop, 0, segBot)
      grad.addColorStop(0, "rgba(255, 255, 255, 0.175)")
      grad.addColorStop(1, "rgba(255, 255, 255, 0.05)")
      ctx.fillStyle = grad

      ctx.beginPath()
      ctx.moveTo(t1[0], t1[1])
      ctx.lineTo(t2[0], t2[1])
      ctx.lineTo(b2[0], b2[1])
      ctx.lineTo(b1[0], b1[1])
      ctx.closePath()
      ctx.fill()
    }
  }

  _drawTrackSurface(ctx, k) {
    const boundaries = this._getTrackBoundaries()
    if (!boundaries) return

    const {
      left: leftBoundary,
      right: rightBoundary,
      rawLeft,
      rawRight,
    } = boundaries

    // Cache raw (pre-loop-removal) boundaries for DRS overlay — it uses
    // index-based access where index = distance in meters.
    this._trackBoundaries = { left: rawLeft, right: rawRight }

    // Draw filled track surface: left boundary forward, right boundary reversed
    ctx.fillStyle = "rgba(55, 55, 55, 0.85)"
    ctx.beginPath()
    ctx.moveTo(leftBoundary[0][0], leftBoundary[0][1])
    for (let i = 1; i < leftBoundary.length; i++) {
      ctx.lineTo(leftBoundary[i][0], leftBoundary[i][1])
    }
    for (let i = rightBoundary.length - 1; i >= 0; i--) {
      ctx.lineTo(rightBoundary[i][0], rightBoundary[i][1])
    }
    ctx.closePath()
    ctx.fill()

    // Draw track edge lines
    ctx.strokeStyle = "rgba(255, 255, 255, 0.5)"
    ctx.lineWidth = 1.5 / k
    ctx.lineCap = "round"
    ctx.lineJoin = "round"

    ctx.beginPath()
    ctx.moveTo(leftBoundary[0][0], leftBoundary[0][1])
    for (let i = 1; i < leftBoundary.length; i++) {
      ctx.lineTo(leftBoundary[i][0], leftBoundary[i][1])
    }
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(rightBoundary[0][0], rightBoundary[0][1])
    for (let i = 1; i < rightBoundary.length; i++) {
      ctx.lineTo(rightBoundary[i][0], rightBoundary[i][1])
    }
    ctx.stroke()
  }

  _drawDrsZones(ctx, data) {
    const bounds = this._trackBoundaries
    if (!bounds || bounds.left.length < 3 || !data || data.length < 2) return

    // Find DRS activation ranges from telemetry transitions
    const zones = []
    let zoneStart = null
    for (let i = 0; i < data.length; i++) {
      if (data[i].drs && zoneStart === null) {
        zoneStart = Math.round(data[i].distance_m || 0)
      } else if (!data[i].drs && zoneStart !== null) {
        zones.push([zoneStart, Math.round(data[i].distance_m || 0)])
        zoneStart = null
      }
    }
    if (zoneStart !== null) {
      zones.push([zoneStart, Math.round(data[data.length - 1].distance_m || 0)])
    }

    // Draw each zone as a single filled polygon using the track boundaries
    const maxIdx = bounds.left.length - 1
    ctx.fillStyle = "rgba(234, 179, 8, 0.25)"
    for (const [from, to] of zones) {
      const start = Math.max(0, Math.min(from, maxIdx))
      const end = Math.max(0, Math.min(to, maxIdx))
      if (end <= start) continue

      ctx.beginPath()
      ctx.moveTo(bounds.left[start][0], bounds.left[start][1])
      for (let j = start + 1; j <= end; j++) {
        ctx.lineTo(bounds.left[j][0], bounds.left[j][1])
      }
      for (let j = end; j >= start; j--) {
        ctx.lineTo(bounds.right[j][0], bounds.right[j][1])
      }
      ctx.closePath()
      ctx.fill()
    }
  }

  _drawRacingLine(ctx, k) {
    const rl = this._activeRacingLine
    if (!rl || rl.length < 3) return

    const project = this._project

    ctx.strokeStyle = "rgba(255, 180, 0, 0.8)"
    ctx.lineWidth = 1.5 / k
    ctx.setLineDash([6 / k, 4 / k])
    ctx.lineCap = "round"
    ctx.lineJoin = "round"

    ctx.beginPath()
    const [x0, y0] = project(rl[0][0], rl[0][1] || 0, rl[0][2])
    ctx.moveTo(x0, y0)
    for (let i = 1; i < rl.length; i++) {
      const [px, py] = project(rl[i][0], rl[i][1] || 0, rl[i][2])
      ctx.lineTo(px, py)
    }
    ctx.closePath()
    ctx.stroke()
    ctx.setLineDash([])
  }

  _drawCornerMarkers(ctx, k) {
    const corners = this.cornersValue
    const cl = this._activeCenterLine
    if (!cl || cl.length < 3 || !corners || corners.length === 0) return

    const project = this._project

    for (const corner of corners) {
      const startIdx = Math.min(
        Math.max(0, Math.round(corner.start_m)),
        cl.length - 1,
      )
      const endIdx = Math.min(
        Math.max(0, Math.round(corner.end_m)),
        cl.length - 1,
      )
      if (startIdx >= endIdx) continue

      // Apex: use precomputed apex_m from corner detection, fallback to midpoint
      let apexIdx
      if (corner.apex_m != null) {
        apexIdx = Math.min(
          Math.max(0, Math.round(corner.apex_m)),
          cl.length - 1,
        )
      } else {
        apexIdx = Math.round((startIdx + endIdx) / 2)
      }

      // The positive normal (nx, nz) points to the left boundary in world space,
      // but zScale is non-inverting, so on screen it appears flipped.
      // outsideSign gives the normal direction that visually points to the
      // outside of the curve on screen.
      const outsideSign = corner.direction === "left" ? 1 : -1

      const markers = [
        { idx: startIdx, isApex: false },
        { idx: apexIdx, isApex: true },
        { idx: endIdx, isApex: false },
      ]

      for (const { idx, isApex } of markers) {
        const [x, y, z, wL, wR] = cl[idx]

        // Compute tangent
        let dx, dz
        if (idx === 0) {
          dx = cl[1][0] - cl[0][0]
          dz = cl[1][2] - cl[0][2]
        } else if (idx === cl.length - 1) {
          dx = cl[idx][0] - cl[idx - 1][0]
          dz = cl[idx][2] - cl[idx - 1][2]
        } else {
          dx = cl[idx + 1][0] - cl[idx - 1][0]
          dz = cl[idx + 1][2] - cl[idx - 1][2]
        }

        const len = Math.sqrt(dx * dx + dz * dz) || 1
        // Perpendicular normal (same convention as _drawTrackSurface)
        const nx = -dz / len
        const nz = dx / len

        // Entry/exit ticks on the outside, apex tick on the inside
        const sign = isApex ? -outsideSign : outsideSign
        const dirx = nx * sign
        const dirz = nz * sign

        // Track half-width on the tick side
        const trackHalfW = sign > 0 ? wL : wR

        const tickLen = isApex ? 5 : 3.5
        const [edgeX, edgeY] = project(
          x + dirx * trackHalfW,
          y,
          z + dirz * trackHalfW,
        )
        const [tickEndX, tickEndY] = project(
          x + dirx * (trackHalfW + tickLen),
          y,
          z + dirz * (trackHalfW + tickLen),
        )

        ctx.strokeStyle = isApex ? "#ff6b35" : "rgba(255, 255, 255, 0.8)"
        ctx.lineWidth = (isApex ? 2.5 : 1.5) / k
        ctx.lineCap = "round"
        ctx.beginPath()
        ctx.moveTo(edgeX, edgeY)
        ctx.lineTo(tickEndX, tickEndY)
        ctx.stroke()
      }
    }
  }

  _drawGrid(ctx, k) {
    if (!this._zScaleInv || !this._perspectiveXInv) return
    const t = this._currentTransform
    const w = this._svgWidth || 0
    const h = this._svgHeight || 0
    if (!w || !h || !t || t.k <= 0) return

    // Visible canvas rect in pre-zoom screen coords (the space xScale/zScale
    // produce). Add a small overdraw so panning never exposes a grid edge.
    const overdraw = 30
    const visMinX = (-t.x - overdraw) / t.k
    const visMinY = (-t.y - overdraw) / t.k
    const visMaxX = (w + overdraw - t.x) / t.k
    const visMaxY = (h + overdraw - t.y) / t.k

    // Map the four canvas corners back to world (x, z). Perspective makes the
    // top of the canvas correspond to a wider world-x range than the bottom,
    // so we sample all four corners and take the bounding rect.
    let xMinW = Infinity,
      xMaxW = -Infinity
    let zMinW = Infinity,
      zMaxW = -Infinity
    const corners = [
      [visMinX, visMinY],
      [visMaxX, visMinY],
      [visMinX, visMaxY],
      [visMaxX, visMaxY],
    ]
    for (const [sx, sy] of corners) {
      const zw = this._zScaleInv(sy)
      const xw = this._perspectiveXInv(sx, zw)
      if (xw < xMinW) xMinW = xw
      if (xw > xMaxW) xMaxW = xw
      if (zw < zMinW) zMinW = zw
      if (zw > zMaxW) zMaxW = zw
    }
    if (!isFinite(xMinW) || !isFinite(zMinW)) return

    // Pick a nice grid step from the visible world span — keeps roughly 10–15
    // lines on screen no matter how far the user has zoomed.
    const visRange = Math.max(xMaxW - xMinW, zMaxW - zMinW)
    if (visRange <= 0) return
    const rawStep = visRange / 13
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
    const niceSteps = [1, 2, 5, 10]
    const step =
      (niceSteps.find((s) => s * magnitude >= rawStep) || 10) * magnitude

    // Snap bounds outward so partial-grid lines aren't clipped at the edges.
    const xStart = Math.floor(xMinW / step) * step - step
    const xEnd = Math.ceil(xMaxW / step) * step + step
    const zStart = Math.floor(zMinW / step) * step - step
    const zEnd = Math.ceil(zMaxW / step) * step + step

    const gp = this._gridProject || this._groundProject
    ctx.strokeStyle = "rgba(255, 255, 255, 0.2)"
    ctx.lineWidth = 0.5 / k

    for (let x = xStart; x <= xEnd; x += step) {
      const [sx0, sy0] = gp(x, zStart)
      const [sx1, sy1] = gp(x, zEnd)
      ctx.beginPath()
      ctx.moveTo(sx0, sy0)
      ctx.lineTo(sx1, sy1)
      ctx.stroke()
    }
    for (let z = zStart; z <= zEnd; z += step) {
      const [sx0, sy0] = gp(xStart, z)
      const [sx1, sy1] = gp(xEnd, z)
      ctx.beginPath()
      ctx.moveTo(sx0, sy0)
      ctx.lineTo(sx1, sy1)
      ctx.stroke()
    }
  }

  // --- Zoom persistence ---

  _saveZoom(transform) {
    try {
      sessionStorage.setItem(
        ZOOM_STORAGE_KEY,
        JSON.stringify({ x: transform.x, y: transform.y, k: transform.k }),
      )
    } catch (_) {
      /* ignore */
    }
  }

  _loadZoom() {
    try {
      const raw = sessionStorage.getItem(ZOOM_STORAGE_KEY)
      if (raw) {
        const { x, y, k } = JSON.parse(raw)
        if (k !== 1 || x !== 0 || y !== 0) return { x, y, k }
      }
    } catch (_) {
      /* ignore */
    }
    return null
  }

  _clearZoom() {
    try {
      sessionStorage.removeItem(ZOOM_STORAGE_KEY)
    } catch (_) {
      /* ignore */
    }
  }

  // --- Minimap ---

  _computeMinimapScales() {
    const data = this._positionData
    if (!data || data.length < 2) return null
    const pad = 2
    const w = this._minimapWidth
    const h = this._minimapHeight
    // Same presentation rotation as the main map, so the overview stays oriented
    // consistently. project(x, z) rotates around the centroid then scales.
    const rotationDeg = this.hasRotationValue ? this.rotationValue : 0
    const rawX = d3.extent(data, (d) => d.pos_x)
    const rawZ = d3.extent(data, (d) => d.pos_z)
    const rot = makeXZRotator(
      rotationDeg,
      (rawX[0] + rawX[1]) / 2,
      (rawZ[0] + rawZ[1]) / 2,
    )
    const rotated = rotationDeg ? data.map((d) => rot(d.pos_x, d.pos_z)) : null
    const xs = rotated ? rotated.map((p) => p[0]) : data.map((d) => d.pos_x)
    const zs = rotated ? rotated.map((p) => p[1]) : data.map((d) => d.pos_z)
    const xExt = d3.extent(xs)
    const zExt = d3.extent(zs)
    const dw = xExt[1] - xExt[0] || 1
    const dh = zExt[1] - zExt[0] || 1
    const availW = w - pad * 2
    const availH = h - pad * 2
    const s = Math.min(availW / dw, availH / dh)
    const ox = pad + (availW - dw * s) / 2
    const oy = pad + (availH - dh * s) / 2
    return {
      project: (x, z) => {
        const [rx, rz] = rot(x, z)
        return [ox + (rx - xExt[0]) * s, oy + (rz - zExt[0]) * s]
      },
    }
  }

  _drawMinimap() {
    const ctx = this._minimapCtx
    if (!ctx) return
    const data = this._positionData
    if (!data || data.length < 2) return
    const w = this._minimapWidth
    const h = this._minimapHeight
    const dpr = this._dpr

    const scales = this._computeMinimapScales()
    if (!scales) return
    this._minimapScales = scales

    ctx.save()
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)

    // Track outline
    const { project } = scales
    ctx.strokeStyle = "rgba(255, 255, 255, 0.3)"
    ctx.lineWidth = 1
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.beginPath()
    const [mx0, my0] = project(data[0].pos_x, data[0].pos_z)
    ctx.moveTo(mx0, my0)
    const stride = Math.max(1, Math.floor(data.length / 500))
    for (let i = stride; i < data.length; i += stride) {
      const [mx, my] = project(data[i].pos_x, data[i].pos_z)
      ctx.lineTo(mx, my)
    }
    const [mxl, myl] = project(
      data[data.length - 1].pos_x,
      data[data.length - 1].pos_z,
    )
    ctx.lineTo(mxl, myl)
    ctx.stroke()

    ctx.restore()
  }

  _drawMinimapDot(index) {
    this._drawMinimap()
    const ctx = this._minimapCtx
    if (!ctx || !this._minimapScales) return
    const data = this._positionData
    if (!data || index < 0 || index >= data.length) return

    const { project } = this._minimapScales
    const d = data[index]
    const [cx, cy] = project(d.pos_x, d.pos_z)

    const dpr = this._dpr
    const teamColor = F1_TEAM_COLORS[this.teamIdValue]

    ctx.save()
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    if (teamColor) {
      ctx.fillStyle = teamColor
      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)"
      ctx.lineWidth = 1.5
    } else {
      ctx.fillStyle = "rgba(255, 255, 255, 0.25)"
      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)"
      ctx.lineWidth = 1.5
    }
    ctx.beginPath()
    ctx.arc(cx, cy, 4, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    ctx.restore()
  }

  _drawFlagOverlays(ctx, k, data) {
    if (!this._detectedEvents) return

    const flagEvents = this._detectedEvents.filter((e) =>
      e.type.endsWith("_flag"),
    )
    for (const evt of flagEvents) {
      const cfg = EVENT_CONFIG[evt.type]
      if (!cfg || !cfg.bgColor) continue

      ctx.strokeStyle = cfg.bgColor
      ctx.lineWidth = OVERLAY_WIDTH / k
      ctx.lineCap = "round"
      ctx.lineJoin = "round"
      ctx.beginPath()
      const [sx, sy] = this._telemetryPoint(data[evt.startIdx])
      ctx.moveTo(sx, sy)
      for (let i = evt.startIdx + 1; i <= evt.endIdx; i++) {
        const [px, py] = this._telemetryPoint(data[i])
        ctx.lineTo(px, py)
      }
      ctx.stroke()
    }
  }

  // --- Telemetry event detection ---

  _detectTelemetryEvents(data) {
    if (this._detectedEvents && this._detectedEventsData === data) return
    this._detectedEventsData = data
    this._detectedEvents = []

    if (!data || data.length < 2) return

    // Detect flag zones (values 1=green, 2=blue, 3=yellow, 4=red)
    let flagStart = null
    let flagValue = null
    for (let i = 0; i < data.length; i++) {
      const v = data[i].vehicle_fia_flags
      const flagType = FLAG_VALUE_MAP[v]
      if (flagType && flagType === FLAG_VALUE_MAP[flagValue]) {
        // Continue current zone
      } else {
        if (flagStart !== null) {
          this._detectedEvents.push({
            type: FLAG_VALUE_MAP[flagValue],
            startIdx: flagStart,
            endIdx: i - 1,
            midIdx: Math.floor((flagStart + i - 1) / 2),
          })
        }
        if (flagType) {
          flagStart = i
          flagValue = v
        } else {
          flagStart = null
          flagValue = null
        }
      }
    }
    if (flagStart !== null) {
      this._detectedEvents.push({
        type: FLAG_VALUE_MAP[flagValue],
        startIdx: flagStart,
        endIdx: data.length - 1,
        midIdx: Math.floor((flagStart + data.length - 1) / 2),
      })
    }

    // Detect damage events (increases in damage channels between consecutive samples)
    const damageEvents = []
    for (let i = 1; i < data.length; i++) {
      const parts = []
      for (const ch of DAMAGE_CHANNELS) {
        const prev = data[i - 1][ch]
        const curr = data[i][ch]
        if (prev !== undefined && curr !== undefined && curr > prev) {
          parts.push(DAMAGE_LABELS[ch])
        }
      }
      if (parts.length > 0) {
        damageEvents.push({ index: i, parts })
      }
    }

    // Merge nearby damage events (within 10 samples)
    if (damageEvents.length > 0) {
      let group = {
        startIdx: damageEvents[0].index,
        endIdx: damageEvents[0].index,
        parts: new Set(damageEvents[0].parts),
      }
      for (let i = 1; i < damageEvents.length; i++) {
        if (damageEvents[i].index - group.endIdx <= 10) {
          group.endIdx = damageEvents[i].index
          damageEvents[i].parts.forEach((p) => group.parts.add(p))
        } else {
          this._detectedEvents.push({
            type: "crash",
            midIdx: Math.floor((group.startIdx + group.endIdx) / 2),
            detail: `Damage: ${[...group.parts].join(", ")}`,
          })
          group = {
            startIdx: damageEvents[i].index,
            endIdx: damageEvents[i].index,
            parts: new Set(damageEvents[i].parts),
          }
        }
      }
      this._detectedEvents.push({
        type: "crash",
        midIdx: Math.floor((group.startIdx + group.endIdx) / 2),
        detail: `Damage: ${[...group.parts].join(", ")}`,
      })
    }

    // Position race events from server data by matching session_time to telemetry timestamps
    const raceEvents = this.raceEventsValue || []
    for (const re of raceEvents) {
      if (!re.session_time || !re.type) continue
      // Binary search for closest telemetry sample by timestamp_ms (which is session_time in seconds)
      const target = re.session_time
      let lo = 0,
        hi = data.length - 1,
        bestIdx = 0
      while (lo <= hi) {
        const mid = (lo + hi) >> 1
        const t = data[mid].timestamp_ms
        if (t < target) {
          lo = mid + 1
        } else {
          hi = mid - 1
        }
        if (
          Math.abs(data[mid].timestamp_ms - target) <
          Math.abs(data[bestIdx].timestamp_ms - target)
        ) {
          bestIdx = mid
        }
      }
      this._detectedEvents.push({
        type: re.type,
        midIdx: bestIdx,
        detail: re.detail || "",
      })
    }
  }

  // --- SVG overlay: event labels, sector markers, car markers ---

  _compensateOverlay(k) {
    // Sector markers — each group is anchored at its track point and scaled inversely
    if (this._sectorGroup) {
      this._sectorGroup.selectAll(".sector-marker").each(function () {
        const el = this
        const x = el.getAttribute("data-x")
        const y = el.getAttribute("data-y")
        d3.select(el).attr("transform", `translate(${x}, ${y}) scale(${1 / k})`)
      })
      // Sector lines in world coordinates — compensate stroke width
      this._sectorGroup
        .selectAll(".sector-line-world")
        .attr("stroke-width", 1.5 / k)
    }

    // Event markers — line + labels in one group, compensated together
    if (this._eventGroup) {
      this._eventGroup.selectAll(".event-marker").each(function () {
        const x = this.getAttribute("data-x")
        const y = this.getAttribute("data-y")
        d3.select(this).attr(
          "transform",
          `translate(${x},${y}) scale(${1 / k})`,
        )
      })
    }

    // Corner labels
    if (this._cornerLabelGroup) {
      this._cornerLabelGroup.selectAll(".corner-marker").each(function () {
        const x = this.getAttribute("data-x")
        const y = this.getAttribute("data-y")
        d3.select(this).attr(
          "transform",
          `translate(${x},${y}) scale(${1 / k})`,
        )
      })
    }

    // DRS labels
    if (this._drsLabelGroup) {
      this._drsLabelGroup.selectAll(".drs-marker").each(function () {
        const x = this.getAttribute("data-x")
        const y = this.getAttribute("data-y")
        d3.select(this).attr(
          "transform",
          `translate(${x},${y}) scale(${1 / k})`,
        )
      })
      this._drsLabelGroup
        .selectAll(".drs-line-world")
        .attr("stroke-width", 1.5 / k)
    }

    // Car markers
    if (this._carMarkerGroup) {
      this._carMarkerGroup
        .selectAll(".car-dot")
        .attr("r", 8 / k)
        .attr("stroke-width", 1.5 / k)
      this._carMarkerGroup
        .selectAll(".car-label-text")
        .attr("font-size", `${11 / k}px`)
      this._carMarkerGroup
        .selectAll(".car-label-bg")
        .attr("rx", 3 / k)
        .attr("ry", 3 / k)
      this._carMarkerGroup.selectAll(".car-line").attr("stroke-width", 1 / k)
    }
  }

  _renderEventLabels(eventGroup, data, project) {
    if (!data || data.length < 2) return

    // Collect all events: telemetry-detected + lap-level
    const allEvents = []

    // Telemetry-positioned events (flags at midpoint, damage at midpoint)
    if (this._detectedEvents) {
      for (const evt of this._detectedEvents) {
        allEvents.push({
          type: evt.type,
          index: evt.midIdx,
          detail: evt.detail,
        })
      }
    }

    // Lap-level events (from data attribute, placed at start/finish = index 0)
    // Skip crash events when telemetry already detected damage (better positioning)
    const hasTelemetryDamage = allEvents.some((e) => e.type === "crash")
    if (this.eventsValue && this.eventsValue.length > 0) {
      for (const evt of this.eventsValue) {
        if (evt.type === "crash" && hasTelemetryDamage) continue
        allEvents.push({ type: evt.type, index: 0, detail: evt.detail })
      }
    }

    if (allEvents.length === 0) return

    // Sort by index
    allEvents.sort((a, b) => a.index - b.index)

    // Group co-located events (within 20 sample indices)
    const groups = []
    let currentGroup = {
      events: [allEvents[0]],
      anchorIndex: allEvents[0].index,
    }
    for (let i = 1; i < allEvents.length; i++) {
      if (Math.abs(allEvents[i].index - currentGroup.anchorIndex) <= 20) {
        currentGroup.events.push(allEvents[i])
      } else {
        groups.push(currentGroup)
        currentGroup = {
          events: [allEvents[i]],
          anchorIndex: allEvents[i].index,
        }
      }
    }
    groups.push(currentGroup)

    // Render each group
    const cl = this._activeCenterLine
    for (const group of groups) {
      const idx = group.anchorIndex
      if (idx < 0 || idx >= data.length) continue

      let px, py, perpNx, perpNy

      // For start/finish events (index 0), use center line for stable positioning
      if (idx === 0 && cl && cl.length > 3) {
        const [cx, cy, cz, wL] = cl[0]
        let tdx, tdz
        tdx = cl[1][0] - cl[0][0]
        tdz = cl[1][2] - cl[0][2]
        const tlen = Math.sqrt(tdx * tdx + tdz * tdz) || 1
        const nx = -tdz / tlen
        const nz = tdx / tlen

        // Anchor at the left track edge (same side as S1 label)
        const overshoot = 3
        ;[px, py] = project(
          cx + nx * (wL + overshoot),
          cy,
          cz + nz * (wL + overshoot),
        )
        const [pcx, pcy] = project(cx, cy, cz)
        const pLen = Math.sqrt((px - pcx) ** 2 + (py - pcy) ** 2) || 1
        perpNx = (px - pcx) / pLen
        perpNy = (py - pcy) / pLen
      } else {
        ;[px, py] = this._telemetryPoint(data[idx])
        const prevIdx = Math.max(0, idx - 1)
        const nextIdx = Math.min(data.length - 1, idx + 1)
        const [pNextX, pNextY] = this._telemetryPoint(data[nextIdx])
        const [pPrevX, pPrevY] = this._telemetryPoint(data[prevIdx])
        const dx = pNextX - pPrevX
        const dy = pNextY - pPrevY
        const len = Math.sqrt(dx * dx + dy * dy) || 1
        perpNx = -dy / len
        perpNy = dx / len
      }

      // Wrapper group anchored at the track point, zoom-compensated as a unit
      const wrapper = eventGroup
        .append("g")
        .attr("class", "event-marker")
        .attr("data-x", px)
        .attr("data-y", py)
        .attr("transform", `translate(${px}, ${py})`)

      // Perpendicular line — skip for start/finish group (overlaps S1 sector line)
      const lineLen = idx === 0 ? 28 : 15
      if (idx !== 0) {
        wrapper
          .append("line")
          .attr("x1", 0)
          .attr("y1", 0)
          .attr("x2", perpNx * lineLen)
          .attr("y2", perpNy * lineLen)
          .attr("stroke", "#666")
          .attr("stroke-width", 1.5)
          .attr("stroke-linecap", "round")
      }

      const iconSize = 10
      const padding = 3
      const labelH = iconSize + padding * 2
      const gap = 2

      // First label center along perpendicular
      const firstLabelOffset = lineLen + labelH / 2 + 2

      // Stack labels along perpendicular direction
      let stackOffset = 0
      for (const evt of group.events) {
        const cfg = EVENT_CONFIG[evt.type]
        if (!cfg) continue
        const detailText = evt.detail || ""
        const textWidth = detailText ? getTextWidth(detailText, 9, "normal") : 0
        const labelW =
          iconSize + padding * 2 + (textWidth > 0 ? textWidth + 4 : 0)

        const lx = perpNx * (firstLabelOffset + stackOffset)
        const ly = perpNy * (firstLabelOffset + stackOffset)

        // Background rect
        wrapper
          .append("rect")
          .attr("x", lx - labelW / 2)
          .attr("y", ly - labelH / 2)
          .attr("width", labelW)
          .attr("height", labelH)
          .attr("rx", 4)
          .attr("ry", 4)
          .attr("fill", cfg.color)

        // Icon (white SVG)
        const iconNS = "http://www.w3.org/2000/svg"
        const iconSvg = document.createElementNS(iconNS, "svg")
        iconSvg.setAttribute("viewBox", "0 0 24 24")
        iconSvg.setAttribute("width", String(iconSize))
        iconSvg.setAttribute("height", String(iconSize))
        iconSvg.setAttribute("fill", "none")
        iconSvg.setAttribute("stroke", "white")
        iconSvg.setAttribute("stroke-width", "2")
        iconSvg.innerHTML = cfg.icon
        const iconX =
          textWidth > 0 ? lx - labelW / 2 + padding : lx - iconSize / 2
        const iconY = ly - iconSize / 2
        iconSvg.setAttribute("x", String(iconX))
        iconSvg.setAttribute("y", String(iconY))
        wrapper.node().appendChild(iconSvg)

        // Detail text (if any)
        if (detailText) {
          wrapper
            .append("text")
            .attr("x", iconX + iconSize + 4)
            .attr("y", ly)
            .attr("dominant-baseline", "central")
            .attr("fill", "white")
            .attr("font-size", "9px")
            .attr("font-weight", "normal")
            .text(detailText)
        }

        stackOffset += labelH + gap
      }
    }
  }

  _renderSectorMarkers(sectorGroup, data, project) {
    const SECTOR_COLOR_PALETTE = [
      "#FF2E92",
      "#6A00FF",
      "#00D4FF",
      "#FFB400",
      "#00FF88",
      "#FF6B00",
    ]
    const cl = this._activeCenterLine

    const boundaries = [{ dist: 0, label: "S1" }]

    // Prefer new N-sector array value
    if (this.hasSectorStartsValue && this.sectorStartsValue.length > 0) {
      this.sectorStartsValue.forEach((dist, i) => {
        if (dist > 0)
          boundaries.push({
            dist,
            label: `S${i + 2}`,
            color: SECTOR_COLOR_PALETTE[i % SECTOR_COLOR_PALETTE.length],
          })
      })
    } else {
      // Fallback: old individual values
      const s2dist = this.hasSector2DistanceValue
        ? this.sector2DistanceValue
        : 0
      const s3dist = this.hasSector3DistanceValue
        ? this.sector3DistanceValue
        : 0
      if (s2dist > 0) {
        boundaries.push({ dist: s2dist, label: "S2" })
      }
      if (s3dist > 0) {
        boundaries.push({ dist: s3dist, label: "S3" })
      }
    }

    boundaries.forEach(({ dist, label }) => {
      // Use center line for position, perpendicular, and width
      if (cl && cl.length > 3) {
        const clIdx = Math.min(Math.max(0, Math.round(dist)), cl.length - 1)
        const [cx, cy, cz, wL, wR] = cl[clIdx]

        // Compute tangent from center line neighbors
        let tdx, tdz
        if (clIdx === 0) {
          tdx = cl[1][0] - cl[0][0]
          tdz = cl[1][2] - cl[0][2]
        } else if (clIdx === cl.length - 1) {
          tdx = cl[clIdx][0] - cl[clIdx - 1][0]
          tdz = cl[clIdx][2] - cl[clIdx - 1][2]
        } else {
          tdx = cl[clIdx + 1][0] - cl[clIdx - 1][0]
          tdz = cl[clIdx + 1][2] - cl[clIdx - 1][2]
        }

        const tlen = Math.sqrt(tdx * tdx + tdz * tdz) || 1
        const nx = -tdz / tlen
        const nz = tdx / tlen

        // Line from right edge to slightly past left edge (label side)
        const overshoot = 3
        const [x1, y1] = project(
          cx + nx * (wL + overshoot),
          cy,
          cz + nz * (wL + overshoot),
        )
        const [x2, y2] = project(cx - nx * wR, cy, cz - nz * wR)

        // Perpendicular direction from track center to label side
        const [px, py] = project(cx, cy, cz)
        const perpX = x1 - px
        const perpY = y1 - py
        const perpLen = Math.sqrt(perpX * perpX + perpY * perpY) || 1
        const perpNx = perpX / perpLen
        const perpNy = perpY / perpLen

        // Sector line spans full track width — drawn in world coordinates
        sectorGroup
          .append("line")
          .attr("class", "sector-line-world")
          .attr("x1", x1)
          .attr("y1", y1)
          .attr("x2", x2)
          .attr("y2", y2)
          .attr("stroke", "rgba(255,255,255,0.3)")
          .attr("stroke-width", 1.5)
          .attr("stroke-linecap", "round")

        // Label in a compensated group anchored at the line endpoint (x1, y1)
        // The label offset is inside the scale(1/k) group so it stays constant screen-size
        const labelOffsetPx = 14

        const g = sectorGroup
          .append("g")
          .attr("class", "sector-marker")
          .attr("data-x", x1)
          .attr("data-y", y1)
          .attr("data-perp-nx", perpNx)
          .attr("data-perp-ny", perpNy)
          .attr("transform", `translate(${x1}, ${y1})`)

        const sectorTextW = getTextWidth(label, 10, "bold")
        const sectorLabelH = 13

        // Position label along perpendicular direction, offset in screen pixels
        const lx = perpNx * labelOffsetPx
        const ly = perpNy * labelOffsetPx

        g.append("rect")
          .attr("class", "sector-label-bg")
          .attr("fill", "rgba(17,24,39,0.85)")
          .attr("rx", 6)
          .attr("ry", 6)
          .attr("x", lx - sectorTextW / 2 - 4)
          .attr("y", ly - sectorLabelH / 2 - 2)
          .attr("width", sectorTextW + 8)
          .attr("height", sectorLabelH + 4)

        g.append("text")
          .attr("class", "sector-label")
          .attr("x", lx)
          .attr("y", ly)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "central")
          .attr("fill", "white")
          .attr("font-size", "10px")
          .attr("font-weight", "bold")
          .text(label)
      } else {
        // Fallback: use telemetry data if no center line available
        const idx = dist === 0 ? 0 : this._findClosestIndex(data, dist)
        if (idx < 0 || idx >= data.length) return

        const [px, py] = this._telemetryPoint(data[idx])

        const prevIdx = Math.max(0, idx - 1)
        const nextIdx = Math.min(data.length - 1, idx + 1)
        const [pNextX, pNextY] = this._telemetryPoint(data[nextIdx])
        const [pPrevX, pPrevY] = this._telemetryPoint(data[prevIdx])
        const dx = pNextX - pPrevX
        const dy = pNextY - pPrevY
        const len = Math.sqrt(dx * dx + dy * dy) || 1
        const perpX = -dy / len
        const perpY = dx / len
        const markerLen = 18

        const g = sectorGroup
          .append("g")
          .attr("class", "sector-marker")
          .attr("data-x", px)
          .attr("data-y", py)
          .attr("transform", `translate(${px}, ${py})`)

        g.append("line")
          .attr("class", "sector-line")
          .attr("x1", 0)
          .attr("y1", 0)
          .attr("x2", perpX * markerLen)
          .attr("y2", perpY * markerLen)
          .attr("stroke", "rgba(255,255,255,0.3)")
          .attr("stroke-width", 2)
          .attr("stroke-linecap", "round")

        const labelX = perpX * (markerLen + 12)
        const labelY = perpY * (markerLen + 12)

        const sectorTextW = getTextWidth(label, 10, "bold")
        const sectorLabelH = 13

        g.append("rect")
          .attr("class", "sector-label-bg")
          .attr("fill", "rgba(17,24,39,0.85)")
          .attr("rx", 6)
          .attr("ry", 6)
          .attr("x", labelX - sectorTextW / 2 - 4)
          .attr("y", labelY - sectorLabelH / 2 - 2)
          .attr("width", sectorTextW + 8)
          .attr("height", sectorLabelH + 4)

        g.append("text")
          .attr("class", "sector-label")
          .attr("x", labelX)
          .attr("y", labelY)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "central")
          .attr("fill", "white")
          .attr("font-size", "10px")
          .attr("font-weight", "bold")
          .text(label)
      }
    })
  }

  _renderDrsLabels(drsGroup, data, project) {
    const cl = this._activeCenterLine
    if (!cl || cl.length < 3 || !data || data.length < 2) return

    // Detect DRS zone starts from telemetry transitions (same logic as _drawDrsZones)
    const zoneStarts = []
    let inZone = false
    for (let i = 0; i < data.length; i++) {
      if (data[i].drs && !inZone) {
        zoneStarts.push(Math.round(data[i].distance_m || 0))
        inZone = true
      } else if (!data[i].drs && inZone) {
        inZone = false
      }
    }

    if (zoneStarts.length === 0) return

    const label = "DRS"
    const drsColor = "#eab308"
    const drsTextW = getTextWidth(label, 10, "bold")
    const drsLabelH = 13

    for (const dist of zoneStarts) {
      const clIdx = Math.min(Math.max(0, Math.round(dist)), cl.length - 1)
      const [cx, cy, cz, wL, wR] = cl[clIdx]

      // Compute tangent from center line neighbors
      let tdx, tdz
      if (clIdx === 0) {
        tdx = cl[1][0] - cl[0][0]
        tdz = cl[1][2] - cl[0][2]
      } else if (clIdx === cl.length - 1) {
        tdx = cl[clIdx][0] - cl[clIdx - 1][0]
        tdz = cl[clIdx][2] - cl[clIdx - 1][2]
      } else {
        tdx = cl[clIdx + 1][0] - cl[clIdx - 1][0]
        tdz = cl[clIdx + 1][2] - cl[clIdx - 1][2]
      }

      const tlen = Math.sqrt(tdx * tdx + tdz * tdz) || 1
      const nx = -tdz / tlen
      const nz = tdx / tlen

      // Line from right edge to slightly past left edge (label side)
      const overshoot = 3
      const [x1, y1] = project(
        cx + nx * (wL + overshoot),
        cy,
        cz + nz * (wL + overshoot),
      )
      const [x2, y2] = project(cx - nx * wR, cy, cz - nz * wR)

      // Perpendicular direction from track center to label side
      const [px, py] = project(cx, cy, cz)
      const perpX = x1 - px
      const perpY = y1 - py
      const perpLen = Math.sqrt(perpX * perpX + perpY * perpY) || 1
      const perpNx = perpX / perpLen
      const perpNy = perpY / perpLen

      // DRS line spans full track width
      drsGroup
        .append("line")
        .attr("class", "drs-line-world")
        .attr("x1", x1)
        .attr("y1", y1)
        .attr("x2", x2)
        .attr("y2", y2)
        .attr("stroke", drsColor)
        .attr("stroke-width", 1.5)
        .attr("stroke-linecap", "round")

      // Label in a compensated group anchored at the line endpoint
      const labelOffsetPx = 14

      const g = drsGroup
        .append("g")
        .attr("class", "drs-marker")
        .attr("data-x", x1)
        .attr("data-y", y1)
        .attr("data-perp-nx", perpNx)
        .attr("data-perp-ny", perpNy)
        .attr("transform", `translate(${x1}, ${y1})`)

      const lx = perpNx * labelOffsetPx
      const ly = perpNy * labelOffsetPx

      g.append("rect")
        .attr("class", "drs-label-bg")
        .attr("fill", drsColor)
        .attr("fill-opacity", 0.8)
        .attr("rx", 6)
        .attr("ry", 6)
        .attr("x", lx - drsTextW / 2 - 4)
        .attr("y", ly - drsLabelH / 2 - 2)
        .attr("width", drsTextW + 8)
        .attr("height", drsLabelH + 4)

      g.append("text")
        .attr("class", "drs-label")
        .attr("x", lx)
        .attr("y", ly)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("fill", "white")
        .attr("font-size", "10px")
        .attr("font-weight", "bold")
        .text(label)
    }
  }

  _renderCornerLabels(cornerGroup, project) {
    const corners = this.cornersValue
    const cl = this._activeCenterLine
    if (!cl || cl.length < 3 || !corners || corners.length === 0) return

    for (const corner of corners) {
      const startIdx = Math.min(
        Math.max(0, Math.round(corner.start_m)),
        cl.length - 1,
      )
      const endIdx = Math.min(
        Math.max(0, Math.round(corner.end_m)),
        cl.length - 1,
      )
      if (startIdx >= endIdx) continue

      // Use static apex for label position, fallback to midpoint
      let apexIdx
      if (corner.apex_m != null) {
        apexIdx = Math.min(
          Math.max(0, Math.round(corner.apex_m)),
          cl.length - 1,
        )
      } else {
        apexIdx = Math.round((startIdx + endIdx) / 2)
      }
      const [mx, my, mz, wL, wR] = cl[apexIdx]

      // Compute tangent at apex
      let dx, dz
      if (apexIdx === 0) {
        dx = cl[1][0] - cl[0][0]
        dz = cl[1][2] - cl[0][2]
      } else if (apexIdx === cl.length - 1) {
        dx = cl[apexIdx][0] - cl[apexIdx - 1][0]
        dz = cl[apexIdx][2] - cl[apexIdx - 1][2]
      } else {
        dx = cl[apexIdx + 1][0] - cl[apexIdx - 1][0]
        dz = cl[apexIdx + 1][2] - cl[apexIdx - 1][2]
      }
      const len = Math.sqrt(dx * dx + dz * dz) || 1
      const nx = -dz / len
      const nz = dx / len

      // Outside of curve: same sign convention as _drawCornerMarkers
      const outsideSign = corner.direction === "left" ? 1 : -1
      const outNx = nx * outsideSign
      const outNz = nz * outsideSign
      const trackHalfW = outsideSign > 0 ? wL : wR

      // Position at track edge + small gap outward
      const edgeGap = 5
      const [anchorX, anchorZ] = project(
        mx + outNx * (trackHalfW + edgeGap),
        my,
        mz + outNz * (trackHalfW + edgeGap),
      )

      // Perpendicular direction in screen space (from track center outward)
      const [centerX, centerZ] = project(mx, my, mz)
      const pLen =
        Math.sqrt((anchorX - centerX) ** 2 + (anchorZ - centerZ) ** 2) || 1
      const perpNx = (anchorX - centerX) / pLen
      const perpNz = (anchorZ - centerZ) / pLen

      // Wrapper group, zoom-compensated
      const g = cornerGroup
        .append("g")
        .attr("class", "corner-marker")
        .attr("data-x", anchorX)
        .attr("data-y", anchorZ)
        .attr("transform", `translate(${anchorX}, ${anchorZ})`)

      // Thin line from track edge outward
      const lineLen = 12
      g.append("line")
        .attr("x1", 0)
        .attr("y1", 0)
        .attr("x2", perpNx * lineLen)
        .attr("y2", perpNz * lineLen)
        .attr("stroke", "rgba(255,255,255,0.4)")
        .attr("stroke-width", 1)
        .attr("stroke-linecap", "round")

      // Circle with corner number
      const circleR = 10
      const circleCx = perpNx * (lineLen + circleR + 2)
      const circleCz = perpNz * (lineLen + circleR + 2)

      g.append("circle")
        .attr("cx", circleCx)
        .attr("cy", circleCz)
        .attr("r", circleR)
        .attr("fill", "rgba(255,255,255,0.1)")
        .attr("stroke", "rgba(255,255,255,0.6)")
        .attr("stroke-width", 1.5)

      g.append("text")
        .attr("x", circleCx)
        .attr("y", circleCz)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("fill", "white")
        .attr("font-size", "9px")
        .attr("font-weight", "bold")
        .attr("pointer-events", "none")
        .text(corner.number)

      // Name label for named corners: a short upright label beside the marker.
      // Named corners show their label at rest; any corner reveals its label on
      // hover. Un-named corners thus look identical to before until hovered.
      const named = corner.name && corner.name.trim()
      let labelGroup = null
      const labelText = named ? corner.name.trim() : null
      if (labelText) {
        // Offset the label outward past the number circle, clamped to stay on
        // the canvas so it never runs off the viewport edge.
        const containerW = this.element.clientWidth || 0
        const containerH = this.element.clientHeight || 0
        const labelGap = circleR + 6
        let lx = circleCx + perpNx * labelGap
        let lz = circleCz + perpNz * labelGap
        // Anchor the text away from the marker; align left/right by side.
        const anchorRight = perpNx < 0
        // Estimate label extent so the clamp keeps the whole label on-canvas.
        const estW = labelText.length * 6.5 + 8
        if (containerW > 0) {
          const screenLx = anchorX + lx
          const minX = anchorRight ? estW : 0
          const maxX = anchorRight ? containerW : containerW - estW
          if (screenLx < minX) lx += minX - screenLx
          if (screenLx > maxX) lx -= screenLx - maxX
        }
        if (containerH > 0) {
          const screenLz = anchorZ + lz
          if (screenLz < 8) lz += 8 - screenLz
          if (screenLz > containerH - 8) lz -= screenLz - (containerH - 8)
        }

        labelGroup = g
          .append("g")
          .attr("class", "corner-name-label")
          .attr("pointer-events", "none")

        // Halo (stroke) under the fill for legibility on the dark map.
        const mkText = (fill, stroke) => {
          const t = labelGroup
            .append("text")
            .attr("x", lx)
            .attr("y", lz)
            .attr("text-anchor", anchorRight ? "end" : "start")
            .attr("dominant-baseline", "central")
            .attr("font-size", "10px")
            .attr("font-weight", "600")
            .attr("fill", fill)
            .text(labelText)
          if (stroke) {
            t.attr("stroke", stroke)
              .attr("stroke-width", 2.5)
              .attr("stroke-linejoin", "round")
              .attr("paint-order", "stroke")
          }
          return t
        }
        mkText("rgba(0,0,0,0.75)", "rgba(0,0,0,0.75)")
        mkText("rgba(255,255,255,0.95)", null)
      }

      // Popover on hover
      const hitArea = g
        .append("circle")
        .attr("cx", circleCx)
        .attr("cy", circleCz)
        .attr("r", circleR + 4)
        .attr("fill", "transparent")
        .attr("cursor", "pointer")

      hitArea.on("mouseenter", () => {
        this._showCornerPopover(
          corner,
          anchorX,
          anchorZ,
          perpNx,
          perpNz,
          lineLen + circleR * 2 + 6,
        )
      })
      hitArea.on("mouseleave", () => {
        this._hideCornerPopover()
      })
    }
  }

  _showCornerPopover(corner, anchorX, anchorZ, perpNx, perpNz, offset) {
    this._hideCornerPopover()

    // Find matching corner analysis data
    const analysis = this.cornerAnalysisValue.find(
      (c) => c.corner_number === corner.number,
    )

    const dirLabel = corner.direction === "left" ? "Left" : "Right"
    const named = corner.name && corner.name.trim()
    const displayName = named ? corner.name.trim() : `Turn ${corner.number}`
    const subline = named ? `T${corner.number} \u00b7 ${dirLabel}` : dirLabel

    const popover = document.createElement("div")
    popover.className = "corner-popover"
    popover.style.cssText =
      "position:fixed;z-index:9999;pointer-events:none;" +
      "background:rgba(17,24,39,0.92);backdrop-filter:blur(8px);" +
      "border:1px solid rgba(255,255,255,0.12);border-radius:8px;" +
      "padding:10px 12px;color:white;font-size:11px;line-height:1.4;" +
      "white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.4)"

    // Header leads with the official name (or "Turn N" fallback), with the
    // number+direction as a mono subline (just direction when un-named).
    let html = `<div style="font-weight:700;font-size:13px;line-height:1.2">${this._escapeHtml(displayName)}</div>`
    html += `<div style="font-family:ui-monospace,monospace;font-size:10px;color:rgba(255,255,255,0.5);margin-top:1px;margin-bottom:6px">${this._escapeHtml(subline)}</div>`

    if (analysis) {
      const entry =
        analysis.entry_speed_kmh != null
          ? Math.round(analysis.entry_speed_kmh)
          : "—"
      const apex =
        analysis.apex_speed_kmh != null
          ? Math.round(analysis.apex_speed_kmh)
          : "—"
      const exit =
        analysis.exit_speed_kmh != null
          ? Math.round(analysis.exit_speed_kmh)
          : "—"

      html += `<div style="display:flex;gap:12px;margin-bottom:4px">`
      html += `<span style="color:rgba(255,255,255,0.5)">Entry</span> <span style="font-weight:600;font-variant-numeric:tabular-nums">${entry}</span>`
      html += `<span style="color:rgba(255,255,255,0.3)">\u2192</span>`
      html += `<span style="color:rgba(255,255,255,0.5)">Apex</span> <span style="font-weight:600;font-variant-numeric:tabular-nums">${apex}</span>`
      html += `<span style="color:rgba(255,255,255,0.3)">\u2192</span>`
      html += `<span style="color:rgba(255,255,255,0.5)">Exit</span> <span style="font-weight:600;font-variant-numeric:tabular-nums">${exit}</span>`
      html += `<span style="color:rgba(255,255,255,0.4)">km/h</span>`
      html += `</div>`

      const stats = []
      if (analysis.max_g_lateral != null) {
        stats.push(
          `<span style="color:rgba(255,255,255,0.5)">Lat G</span> ${analysis.max_g_lateral.toFixed(2)}`,
        )
      }
      if (analysis.time_ms != null) {
        stats.push(
          `<span style="color:rgba(255,255,255,0.5)">Time</span> ${(analysis.time_ms / 1000).toFixed(3)}s`,
        )
      }
      if (analysis.time_loss_ms != null && analysis.time_loss_ms !== 0) {
        const sign = analysis.time_loss_ms > 0 ? "+" : ""
        const color = analysis.time_loss_ms > 0 ? "#f87171" : "#4ade80"
        stats.push(
          `<span style="color:rgba(255,255,255,0.5)">Delta</span> <span style="color:${color}">${sign}${(analysis.time_loss_ms / 1000).toFixed(3)}s</span>`,
        )
      }
      if (stats.length > 0) {
        html += `<div style="display:flex;gap:12px;margin-bottom:4px;font-variant-numeric:tabular-nums">${stats.join("")}</div>`
      }

      const factors = []
      if (analysis.brake_point_error != null) {
        const v = analysis.brake_point_error
        const sign = v > 0 ? "+" : ""
        const color = v > 0 ? "#60a5fa" : "#fbbf24"
        factors.push(
          `<span style="color:rgba(255,255,255,0.5)">Brake</span> <span style="color:${color}">${sign}${v.toFixed(1)}m</span>`,
        )
      }
      if (analysis.throttle_pickup_delay != null) {
        const v = analysis.throttle_pickup_delay
        const sign = v > 0 ? "+" : ""
        const color = v <= 0 ? "#4ade80" : "#fbbf24"
        factors.push(
          `<span style="color:rgba(255,255,255,0.5)">Throttle</span> <span style="color:${color}">${sign}${v.toFixed(1)}m</span>`,
        )
      }
      if (analysis.min_speed_deficit != null) {
        const v = analysis.min_speed_deficit
        const sign = v > 0 ? "+" : ""
        const color = v >= 0 ? "#4ade80" : "#fbbf24"
        factors.push(
          `<span style="color:rgba(255,255,255,0.5)">Apex</span> <span style="color:${color}">${sign}${v.toFixed(1)} km/h</span>`,
        )
      }
      if (factors.length > 0) {
        html += `<div style="display:flex;gap:12px;font-variant-numeric:tabular-nums">${factors.join("")}</div>`
      }

      if (
        analysis.brake_shape &&
        analysis.brake_shape !== "progressive" &&
        analysis.brake_shape !== "threshold"
      ) {
        const colors = { panic: "#f87171", pumping: "#fbbf24" }
        const color = colors[analysis.brake_shape] || "rgba(255,255,255,0.5)"
        html += `<div style="margin-top:2px"><span style="color:${color};font-size:10px;text-transform:uppercase;letter-spacing:0.5px">${analysis.brake_shape} braking</span></div>`
      }

      if (analysis.recommendations && analysis.recommendations.length > 0) {
        html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.1)">`
        for (const rec of analysis.recommendations) {
          html += `<div style="color:rgba(255,255,255,0.7);font-size:10px">\u2022 ${this._escapeHtml(typeof rec === "string" ? rec : rec.text || rec.message || JSON.stringify(rec))}</div>`
        }
        html += `</div>`
      }
    } else {
      html += `<div style="color:rgba(255,255,255,0.4)">No analysis data</div>`
    }

    popover.innerHTML = html

    // Position in screen space, accounting for current zoom transform
    const { x: tx, y: ty, k } = this._currentTransform
    const screenX = anchorX * k + tx + perpNx * offset
    const screenY = anchorZ * k + ty + perpNz * offset

    // Convert container-relative coords to fixed (viewport) coords
    const containerRect = this.element.getBoundingClientRect()
    const fixedX = containerRect.left + screenX + 8
    const fixedY = containerRect.top + screenY - 10

    popover.style.left = `${fixedX}px`
    popover.style.top = `${fixedY}px`

    // Append to body so it's not clipped by overflow:hidden ancestors
    document.body.appendChild(popover)
    this._cornerPopover = popover

    // Adjust if overflowing the viewport
    requestAnimationFrame(() => {
      if (!this._cornerPopover) return
      const popRect = popover.getBoundingClientRect()
      if (popRect.right > window.innerWidth - 8) {
        popover.style.left = `${fixedX - popRect.width - 16}px`
      }
      if (popRect.bottom > window.innerHeight - 8) {
        popover.style.top = `${fixedY - popRect.height + 20}px`
      }
    })
  }

  _hideCornerPopover() {
    if (this._cornerPopover) {
      this._cornerPopover.remove()
      this._cornerPopover = null
    }
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _findClosestIndex(data, targetDist) {
    let bestIdx = 0
    let bestDiff = Infinity
    for (let i = 0; i < data.length; i++) {
      const diff = Math.abs((data[i].distance_m || 0) - targetDist)
      if (diff < bestDiff) {
        bestDiff = diff
        bestIdx = i
      }
    }
    return bestIdx
  }

  // --- Car markers (SVG overlay) ---

  _setupCarMarker(markerGroup) {
    const teamColor = F1_TEAM_COLORS[this.teamIdValue] || FALLBACK_COLOR
    const driverName = this.driverNameValue || ""

    this._carLine = markerGroup
      .append("line")
      .attr("class", "car-line")
      .attr("stroke", "white")
      .attr("stroke-width", 1)
      .style("display", "none")

    this._carLabelBg = markerGroup
      .append("rect")
      .attr("class", "car-label-bg")
      .attr("fill", "rgba(0,0,0,0.75)")
      .attr("rx", 3)
      .attr("ry", 3)
      .style("display", "none")

    this._carLabelText = markerGroup
      .append("text")
      .attr("class", "car-label-text")
      .attr("fill", "white")
      .attr("font-size", "11px")
      .attr("font-weight", "600")
      .text(driverName)
      .style("display", "none")

    this._carDot = markerGroup
      .append("circle")
      .attr("class", "car-dot")
      .attr("r", 8)
      .attr("fill", teamColor)
      .attr("stroke", "rgba(255, 255, 255, 0.5)")
      .attr("stroke-width", 1.5)
      .style("display", "none")
  }

  _setupComparisonCarMarkers(markerGroup, laps) {
    this._comparisonMarkers = laps.map((lap) => {
      const line = markerGroup
        .append("line")
        .attr("class", "car-line")
        .attr("stroke", "white")
        .attr("stroke-width", 1)
        .style("display", "none")
      const bg = markerGroup
        .append("rect")
        .attr("class", "car-label-bg")
        .attr("fill", "rgba(0,0,0,0.75)")
        .attr("rx", 3)
        .attr("ry", 3)
        .style("display", "none")
      const text = markerGroup
        .append("text")
        .attr("class", "car-label-text")
        .attr("fill", "white")
        .attr("font-size", "11px")
        .attr("font-weight", "600")
        .text(lap.driverName || "")
        .style("display", "none")
      const dot = markerGroup
        .append("circle")
        .attr("class", "car-dot")
        .attr("r", 8)
        .attr("fill", lap.color)
        .attr("stroke", "rgba(255, 255, 255, 0.5)")
        .attr("stroke-width", 1.5)
        .style("display", "none")
      return { line, bg, text, dot }
    })
  }

  _positionCarMarker(dot, line, bg, textEl, cx, cy, k) {
    const offsetX = 15 / k
    const offsetY = -20 / k
    const labelX = cx + offsetX
    const labelY = cy + offsetY

    dot.attr("cx", cx).attr("cy", cy).style("display", null)
    line
      .attr("x1", cx)
      .attr("y1", cy)
      .attr("x2", labelX)
      .attr("y2", labelY)
      .style("display", null)

    const textX = labelX + 4 / k
    const textY = labelY + 4 / k
    textEl.attr("x", textX).attr("y", textY).style("display", null)

    const fontSize = 11 / k
    const labelText = textEl.text()
    const textW = getTextWidth(labelText, fontSize, "600")
    const lineH = fontSize * 1.2
    const pad = 3 / k
    bg.attr("x", textX - pad)
      .attr("y", textY - lineH / 2 - pad)
      .attr("width", textW + pad * 2)
      .attr("height", lineH + pad * 2)
      .style("display", null)
  }

  _hideCarMarker(dot, line, bg, textEl) {
    dot.style("display", "none")
    line.style("display", "none")
    bg.style("display", "none")
    textEl.style("display", "none")
  }

  // --- Hover / Leave ---

  onHover(event) {
    const { index } = event.detail
    const k = this._currentZoomK

    this._drawMinimapDot(index)

    let primaryCx, primaryCy
    if (
      (this.comparisonValue || this._isComparison) &&
      this._comparisonMarkers
    ) {
      this._comparisonLapsData.forEach((lap, i) => {
        const data = lap.telemetry
        const marker = this._comparisonMarkers[i]
        if (!data || !marker || index < 0 || index >= data.length) {
          if (marker)
            this._hideCarMarker(marker.dot, marker.line, marker.bg, marker.text)
          return
        }
        const d = data[index]
        if (d) {
          const [cx, cy] = this._telemetryPoint(d)
          if (i === 0) {
            primaryCx = cx
            primaryCy = cy
          }
          this._positionCarMarker(
            marker.dot,
            marker.line,
            marker.bg,
            marker.text,
            cx,
            cy,
            k,
          )
        }
      })
    } else {
      const data = this._trackData
      if (!data || !this._carDot || index < 0 || index >= data.length) return

      ;[primaryCx, primaryCy] = this._telemetryPoint(data[index])
      this._positionCarMarker(
        this._carDot,
        this._carLine,
        this._carLabelBg,
        this._carLabelText,
        primaryCx,
        primaryCy,
        k,
      )
    }

    // Auto-pan to keep the car visible when zoomed in
    if (primaryCx != null && k > 1) {
      this._autoPanToPoint(primaryCx, primaryCy)
    }
  }

  _autoPanToPoint(cx, cy) {
    const t = this._currentTransform || d3.zoomIdentity
    const k = t.k
    const vw = this._svgWidth
    const vh = this._svgHeight

    // Car position in screen (pixel) space
    const screenX = cx * k + t.x
    const screenY = cy * k + t.y

    // Inset margin — car must stay within this box
    const margin = Math.min(vw, vh) * 0.2

    let dx = 0,
      dy = 0
    if (screenX < margin) dx = margin - screenX
    else if (screenX > vw - margin) dx = vw - margin - screenX
    if (screenY < margin) dy = margin - screenY
    else if (screenY > vh - margin) dy = vh - margin - screenY

    // Nothing to do if car is inside the safe zone
    if (dx === 0 && dy === 0) return

    const newTransform = d3.zoomIdentity.translate(t.x + dx, t.y + dy).scale(k)
    this._svg.call(this.zoom.transform, newTransform)
  }

  onLeave() {
    // Don't hide the car marker if replay is driving the position
    if (this._replayActive) return

    this._drawMinimap()

    if (
      (this.comparisonValue || this._isComparison) &&
      this._comparisonMarkers
    ) {
      this._comparisonMarkers.forEach((m) => {
        this._hideCarMarker(m.dot, m.line, m.bg, m.text)
      })
    } else if (this._carDot) {
      this._hideCarMarker(
        this._carDot,
        this._carLine,
        this._carLabelBg,
        this._carLabelText,
      )
    }
  }

  // --- Replay integration (Pit Wall) ---

  _onPitWallUpdate(event) {
    const data = event.detail
    if (!data || data.type !== "race_state" || !data.cars) return

    const trackData = this._trackData
    if (!trackData || trackData.length < 2) return
    if (!this._carDot) return

    const trackedIdx = data.tracked_car_index
    if (trackedIdx === undefined) return

    const trackedCar = data.cars.find((c) => c.car_index === trackedIdx)
    if (!trackedCar) return

    const wx = trackedCar.world_position_x
    const wz = trackedCar.world_position_z

    const index = this._findClosestPositionIndex(trackData, wx, wz)
    if (index < 0) return

    this._replayActive = true
    this.onHover({ detail: { index } })

    // Bubble up so telemetry charts also show the cursor at this position
    this.element.dispatchEvent(
      new CustomEvent("telemetry:hover", {
        bubbles: true,
        detail: { index, source: this.element },
      }),
    )
  }

  _findClosestPositionIndex(data, wx, wz) {
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < data.length; i++) {
      const dx = data[i].pos_x - wx
      const dz = data[i].pos_z - wz
      const dist = dx * dx + dz * dz
      if (dist < bestDist) {
        bestDist = dist
        bestIdx = i
      }
    }
    return bestIdx
  }

  // --- Scheduling ---

  _scheduleRender() {
    if (this._renderRAF) return
    if (this._redrawRAF) {
      cancelAnimationFrame(this._redrawRAF)
      this._redrawRAF = null
    }
    this._renderRAF = requestAnimationFrame(() => {
      this._renderRAF = null
      this.render()
    })
  }

  _scheduleRedraw() {
    if (this._redrawRAF || this._renderRAF) return
    this._redrawRAF = requestAnimationFrame(() => {
      this._redrawRAF = null
      this._redrawCanvas()
    })
  }
}
